import type { PlayerSeason, Role } from "./players";

export type DataLevel = "sourced" | "curated-high" | "curated-medium" | "derived";

export interface PlayerProfile {
  dataLevel: DataLevel;
  pace: number;
  shooting: number;
  passing: number;
  dribbling: number;
  defending: number;
  physical: number;
  finishing: number;
  creativity: number;
  workRate: number;
  legacy: number;
  chemistryTags: string[];
  sourceNote: string;
}

type ProfileOverride = Partial<Omit<PlayerProfile, "dataLevel" | "sourceNote">> &
  Pick<PlayerProfile, "dataLevel" | "sourceNote">;

const profileOverrides: Record<string, ProfileOverride> = {
  "maradona-nap-86": {
    dataLevel: "curated-high",
    dribbling: 99,
    passing: 96,
    shooting: 94,
    finishing: 94,
    creativity: 99,
    workRate: 89,
    legacy: 100,
    chemistryTags: ["Diego", "Scudetto Napoli", "Numero 10"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 29 presenze, 10 gol.",
  },
  "albertosi-cag-69": {
    dataLevel: "curated-high",
    defending: 93,
    physical: 88,
    passing: 72,
    workRate: 93,
    legacy: 94,
    chemistryTags: ["Cagliari 1970", "Portiere", "Scudetto isola"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 30 presenze, tutte da titolare.",
  },
  "niccolai-cag-69": {
    dataLevel: "curated-medium",
    pace: 75,
    passing: 72,
    defending: 88,
    physical: 90,
    workRate: 89,
    legacy: 88,
    chemistryTags: ["Cagliari 1970", "Marcatura", "Difesa storica"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 29 presenze, tutte da titolare.",
  },
  "zignoli-cag-69": {
    dataLevel: "curated-medium",
    pace: 78,
    passing: 75,
    defending: 85,
    physical: 84,
    creativity: 74,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Cagliari 1970", "Fascia", "Scudetto"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 25 presenze, 24 da titolare.",
  },
  "martiradonna-cag-69": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 73,
    defending: 87,
    physical: 89,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Cagliari 1970", "Muro", "Amsicora"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 28 presenze, tutte da titolare.",
  },
  "tomasini-cag-69": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 72,
    defending: 84,
    physical: 84,
    workRate: 85,
    legacy: 84,
    chemistryTags: ["Cagliari 1970", "Difesa", "Rotazione"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 17 presenze, tutte da titolare.",
  },
  "cera-cag-69": {
    dataLevel: "curated-high",
    pace: 76,
    passing: 91,
    dribbling: 82,
    defending: 91,
    physical: 86,
    creativity: 90,
    workRate: 92,
    legacy: 92,
    chemistryTags: ["Cagliari 1970", "Libero", "Regia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 27 presenze, tutte da titolare.",
  },
  "nene-cag-69": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 84,
    passing: 88,
    dribbling: 90,
    physical: 82,
    finishing: 83,
    creativity: 91,
    workRate: 88,
    legacy: 91,
    chemistryTags: ["Cagliari 1970", "Brasile", "Tecnica"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 28 presenze, 3 gol.",
  },
  "greatti-cag-69": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 76,
    passing: 84,
    dribbling: 80,
    defending: 82,
    physical: 84,
    creativity: 82,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Cagliari 1970", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 27 presenze, 1 gol.",
  },
  "domenghini-cag-69": {
    dataLevel: "curated-high",
    pace: 88,
    shooting: 88,
    passing: 86,
    dribbling: 89,
    physical: 84,
    finishing: 87,
    creativity: 88,
    workRate: 92,
    legacy: 92,
    chemistryTags: ["Cagliari 1970", "Ala", "Nazionale"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 30 presenze, 8 gol.",
  },
  "gori-cag-69": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 86,
    passing: 80,
    dribbling: 82,
    physical: 85,
    finishing: 86,
    creativity: 81,
    workRate: 89,
    legacy: 88,
    chemistryTags: ["Cagliari 1970", "Spalla", "Riva"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 30 presenze, 6 gol.",
  },
  "riva-cag-69": {
    dataLevel: "curated-high",
    pace: 90,
    shooting: 98,
    passing: 84,
    dribbling: 91,
    physical: 94,
    finishing: 99,
    creativity: 88,
    workRate: 92,
    legacy: 100,
    chemistryTags: ["Cagliari 1970", "Rombo di Tuono", "Scudetto"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 28 presenze, 21 gol.",
  },
  "brugnera-cag-69": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 82,
    passing: 82,
    dribbling: 82,
    physical: 80,
    finishing: 82,
    creativity: 83,
    workRate: 85,
    legacy: 84,
    chemistryTags: ["Cagliari 1970", "Cambio", "Profondita"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Cagliari 1969-70: 19 presenze, 3 gol.",
  },
  "castellini-tor-75": {
    dataLevel: "curated-high",
    defending: 92,
    physical: 87,
    passing: 68,
    workRate: 93,
    legacy: 93,
    chemistryTags: ["Torino 1976", "Portiere", "Scudetto granata"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 29 presenze e titolo Serie A.",
  },
  "mozzini-tor-75": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 71,
    defending: 89,
    physical: 90,
    workRate: 90,
    legacy: 88,
    chemistryTags: ["Torino 1976", "Difesa", "Radice"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 29 presenze nella miglior difesa.",
  },
  "salvadori-tor-75": {
    dataLevel: "curated-medium",
    pace: 79,
    passing: 80,
    defending: 86,
    physical: 84,
    creativity: 78,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Torino 1976", "Fascia", "Duttilita"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 30 presenze.",
  },
  "caporale-tor-75": {
    dataLevel: "curated-medium",
    pace: 73,
    passing: 70,
    defending: 86,
    physical: 88,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Torino 1976", "Marcatura", "Scudetto"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 28 presenze.",
  },
  "santin-tor-75": {
    dataLevel: "curated-medium",
    pace: 72,
    passing: 72,
    defending: 85,
    physical: 86,
    workRate: 86,
    legacy: 84,
    chemistryTags: ["Torino 1976", "Difesa", "Esperienza"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 25 presenze.",
  },
  "psala-tor-75": {
    dataLevel: "curated-high",
    pace: 84,
    passing: 84,
    dribbling: 82,
    defending: 84,
    physical: 85,
    creativity: 82,
    workRate: 94,
    legacy: 89,
    chemistryTags: ["Torino 1976", "Pressing", "Radice"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 30 presenze.",
  },
  "csala-tor-75": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 83,
    passing: 92,
    dribbling: 93,
    physical: 80,
    finishing: 82,
    creativity: 94,
    workRate: 89,
    legacy: 94,
    chemistryTags: ["Torino 1976", "Poeta del gol", "Cross"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 29 presenze, 1 gol.",
  },
  "pecci-tor-75": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 80,
    passing: 91,
    dribbling: 86,
    defending: 82,
    physical: 82,
    finishing: 78,
    creativity: 91,
    workRate: 91,
    legacy: 91,
    chemistryTags: ["Torino 1976", "Regia", "Scudetto granata"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 29 presenze, 2 gol.",
  },
  "zaccarelli-tor-75": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 84,
    passing: 87,
    dribbling: 84,
    defending: 80,
    physical: 84,
    finishing: 83,
    creativity: 86,
    workRate: 91,
    legacy: 90,
    chemistryTags: ["Torino 1976", "Mezzala", "Inserimento"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 28 presenze, 4 gol.",
  },
  "pulici-tor-75": {
    dataLevel: "curated-high",
    pace: 88,
    shooting: 98,
    passing: 80,
    dribbling: 88,
    physical: 91,
    finishing: 98,
    creativity: 84,
    workRate: 91,
    legacy: 98,
    chemistryTags: ["Torino 1976", "Pulici-Graziani", "Capocannoniere"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 30 presenze, 21 gol.",
  },
  "graziani-tor-75": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 93,
    passing: 82,
    dribbling: 86,
    physical: 90,
    finishing: 94,
    creativity: 84,
    workRate: 92,
    legacy: 94,
    chemistryTags: ["Torino 1976", "Pulici-Graziani", "Ciccio"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 29 presenze, 15 gol.",
  },
  "gorin-tor-75": {
    dataLevel: "curated-medium",
    pace: 77,
    shooting: 76,
    passing: 80,
    dribbling: 78,
    defending: 77,
    physical: 80,
    creativity: 79,
    workRate: 84,
    legacy: 83,
    chemistryTags: ["Torino 1976", "Rotazione", "Radice"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Torino 1975-76: 12 presenze, 1 gol.",
  },
  "mazzantini-per-98": {
    dataLevel: "curated-medium",
    defending: 83,
    physical: 82,
    passing: 64,
    workRate: 84,
    legacy: 82,
    chemistryTags: ["Perugia 1999", "Portiere", "Provincia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 17 presenze.",
  },
  "matrecano-per-98": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 70,
    defending: 85,
    physical: 88,
    workRate: 87,
    legacy: 84,
    chemistryTags: ["Perugia 1999", "Marcatura", "Grifoni"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 29 presenze, 1 gol.",
  },
  "colonnello-per-98": {
    dataLevel: "curated-medium",
    pace: 80,
    passing: 78,
    defending: 82,
    physical: 82,
    creativity: 77,
    workRate: 86,
    legacy: 82,
    chemistryTags: ["Perugia 1999", "Fascia", "Equilibrio"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 29 presenze.",
  },
  "rivas-per-98": {
    dataLevel: "curated-medium",
    pace: 75,
    passing: 72,
    defending: 84,
    physical: 86,
    workRate: 85,
    legacy: 83,
    chemistryTags: ["Perugia 1999", "Argentina", "Difesa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 26 presenze.",
  },
  "ripa-per-98": {
    dataLevel: "curated-medium",
    pace: 72,
    passing: 70,
    defending: 81,
    physical: 83,
    workRate: 81,
    legacy: 79,
    chemistryTags: ["Perugia 1999", "Difesa", "Rotazione"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 21 presenze.",
  },
  "rapaic-per-98": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 88,
    passing: 86,
    dribbling: 90,
    physical: 81,
    finishing: 88,
    creativity: 89,
    workRate: 86,
    legacy: 90,
    chemistryTags: ["Perugia 1999", "Croazia", "Sinistro"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 34 presenze, 9 gol.",
  },
  "nakata-per-98": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 90,
    passing: 91,
    dribbling: 92,
    physical: 82,
    finishing: 89,
    creativity: 93,
    workRate: 90,
    legacy: 95,
    chemistryTags: ["Perugia 1999", "Samurai", "Calcio globale"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 33 presenze, 10 gol.",
  },
  "olive-per-98": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 76,
    passing: 82,
    dribbling: 78,
    defending: 80,
    physical: 83,
    creativity: 80,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Perugia 1999", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 27 presenze, 2 gol.",
  },
  "petrachi-per-98": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 82,
    passing: 80,
    dribbling: 84,
    physical: 80,
    finishing: 82,
    creativity: 82,
    workRate: 85,
    legacy: 83,
    chemistryTags: ["Perugia 1999", "Esterno", "Provincia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 28 presenze, 5 gol.",
  },
  "tedesco-per-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 81,
    passing: 81,
    dribbling: 78,
    defending: 78,
    physical: 82,
    finishing: 80,
    creativity: 80,
    workRate: 87,
    legacy: 82,
    chemistryTags: ["Perugia 1999", "Inserimento", "Mediana"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 23 presenze, 4 gol.",
  },
  "bucchi-per-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 83,
    passing: 74,
    dribbling: 78,
    physical: 82,
    finishing: 84,
    creativity: 75,
    workRate: 82,
    legacy: 82,
    chemistryTags: ["Perugia 1999", "Area", "Punta"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 27 presenze, 5 gol.",
  },
  "kaviedes-per-98": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 81,
    passing: 73,
    dribbling: 82,
    physical: 78,
    finishing: 82,
    creativity: 77,
    workRate: 80,
    legacy: 81,
    chemistryTags: ["Perugia 1999", "Ecuador", "Colpo esotico"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Perugia 1998-99: 14 presenze, 3 gol.",
  },
  "mancini-bari-99": {
    dataLevel: "curated-medium",
    defending: 83,
    physical: 82,
    passing: 64,
    workRate: 86,
    legacy: 82,
    chemistryTags: ["Bari 2000", "Portiere", "San Nicola"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 34 presenze.",
  },
  "delgrosso-bari-99": {
    dataLevel: "curated-medium",
    pace: 78,
    passing: 77,
    defending: 81,
    physical: 82,
    creativity: 75,
    workRate: 86,
    legacy: 80,
    chemistryTags: ["Bari 2000", "Fascia", "Provincia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 30 presenze.",
  },
  "garzya-bari-99": {
    dataLevel: "curated-medium",
    pace: 73,
    passing: 70,
    defending: 83,
    physical: 84,
    workRate: 84,
    legacy: 81,
    chemistryTags: ["Bari 2000", "Marcatura", "Fascetti"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 24 presenze.",
  },
  "innocenti-bari-99": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 78,
    passing: 73,
    defending: 84,
    physical: 85,
    finishing: 78,
    workRate: 86,
    legacy: 83,
    chemistryTags: ["Bari 2000", "Difensore goleador", "Area"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 28 presenze, 4 gol.",
  },
  "derosa-bari-99": {
    dataLevel: "curated-medium",
    pace: 72,
    passing: 75,
    defending: 84,
    physical: 86,
    creativity: 74,
    workRate: 85,
    legacy: 83,
    chemistryTags: ["Bari 2000", "Libero", "Difesa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 27 presenze.",
  },
  "dandersson-bari-99": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 82,
    passing: 86,
    dribbling: 80,
    defending: 84,
    physical: 86,
    finishing: 81,
    creativity: 84,
    workRate: 91,
    legacy: 87,
    chemistryTags: ["Bari 2000", "Svezia", "Mediana"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 32 presenze, 5 gol.",
  },
  "collauto-bari-99": {
    dataLevel: "curated-medium",
    pace: 79,
    passing: 77,
    dribbling: 76,
    defending: 77,
    physical: 80,
    creativity: 76,
    workRate: 86,
    legacy: 79,
    chemistryTags: ["Bari 2000", "Esterno", "Lavoro"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 26 presenze.",
  },
  "marcolini-bari-99": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 77,
    passing: 83,
    dribbling: 78,
    defending: 77,
    physical: 80,
    creativity: 82,
    workRate: 85,
    legacy: 82,
    chemistryTags: ["Bari 2000", "Geometria", "Mediana"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 27 presenze, 2 gol.",
  },
  "perrotta-bari-99": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 78,
    passing: 83,
    dribbling: 82,
    defending: 82,
    physical: 84,
    creativity: 82,
    workRate: 93,
    legacy: 86,
    chemistryTags: ["Bari 2000", "Corsa", "Futuro mondiale"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 31 presenze, 1 gol.",
  },
  "osmanovski-bari-99": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 84,
    passing: 80,
    dribbling: 85,
    physical: 79,
    finishing: 85,
    creativity: 82,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Bari 2000", "Svezia", "Seconda punta"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 24 presenze, 7 gol.",
  },
  "spinesi-bari-99": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 84,
    passing: 74,
    dribbling: 78,
    physical: 82,
    finishing: 85,
    creativity: 75,
    workRate: 82,
    legacy: 82,
    chemistryTags: ["Bari 2000", "Area", "Punta"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 23 presenze, 5 gol.",
  },
  "ferrari-bari-99": {
    dataLevel: "curated-medium",
    pace: 78,
    passing: 74,
    defending: 84,
    physical: 84,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Bari 2000", "Giovane", "Difesa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 26 presenze.",
  },
  "neqrouz-bari-99": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 76,
    passing: 72,
    defending: 83,
    physical: 87,
    finishing: 76,
    workRate: 84,
    legacy: 83,
    chemistryTags: ["Bari 2000", "Marocco", "Difesa fisica"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 20 presenze, 3 gol.",
  },
  "cassano-bari-99": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 86,
    passing: 90,
    dribbling: 94,
    physical: 76,
    finishing: 86,
    creativity: 94,
    workRate: 78,
    legacy: 93,
    chemistryTags: ["Bari 2000", "Bari Vecchia", "Cassano-Inter"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 21 presenze, 3 gol.",
  },
  "masinga-bari-99": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 80,
    passing: 74,
    dribbling: 76,
    physical: 88,
    finishing: 81,
    creativity: 74,
    workRate: 80,
    legacy: 83,
    chemistryTags: ["Bari 2000", "Sudafrica", "Torre"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 11 presenze, 1 gol.",
  },
  "enyinnaya-bari-99": {
    dataLevel: "curated-medium",
    pace: 85,
    shooting: 80,
    passing: 72,
    dribbling: 82,
    physical: 78,
    finishing: 81,
    creativity: 75,
    workRate: 79,
    legacy: 81,
    chemistryTags: ["Bari 2000", "Nigeria", "Cassano-Inter"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Bari 1999-00: 12 presenze, 2 gol.",
  },
  "garella-ver-84": {
    dataLevel: "curated-high",
    defending: 91,
    physical: 86,
    passing: 66,
    workRate: 92,
    legacy: 93,
    chemistryTags: ["Verona 1985", "Portiere", "Miracolo"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 30 presenze, tutte da titolare.",
  },
  "tricella-ver-84": {
    dataLevel: "curated-high",
    pace: 77,
    passing: 84,
    dribbling: 78,
    defending: 91,
    physical: 88,
    creativity: 83,
    workRate: 92,
    legacy: 91,
    chemistryTags: ["Verona 1985", "Capitano", "Libero"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 30 presenze, tutte da titolare.",
  },
  "lmarangon-ver-84": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 77,
    passing: 82,
    dribbling: 80,
    defending: 85,
    physical: 84,
    creativity: 81,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Verona 1985", "Fascia", "Scudetto"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 29 presenze, 2 gol.",
  },
  "fontolan-ver-84": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 75,
    defending: 86,
    physical: 87,
    workRate: 89,
    legacy: 85,
    chemistryTags: ["Verona 1985", "Difesa", "Linea"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 28 presenze, 1 gol.",
  },
  "briegel-ver-84": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 89,
    passing: 84,
    dribbling: 83,
    defending: 90,
    physical: 96,
    finishing: 88,
    creativity: 85,
    workRate: 95,
    legacy: 94,
    chemistryTags: ["Verona 1985", "Panzer", "Germania"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 27 presenze, 9 gol.",
  },
  "volpati-ver-84": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 70,
    passing: 84,
    dribbling: 79,
    defending: 84,
    physical: 85,
    creativity: 82,
    workRate: 92,
    legacy: 86,
    chemistryTags: ["Verona 1985", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 30 presenze, 29 da titolare.",
  },
  "digennaro-ver-84": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 84,
    passing: 93,
    dribbling: 88,
    defending: 77,
    physical: 82,
    finishing: 82,
    creativity: 94,
    workRate: 90,
    legacy: 92,
    chemistryTags: ["Verona 1985", "Regia", "Bagnoli"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 29 presenze, 4 gol.",
  },
  "fanna-ver-84": {
    dataLevel: "curated-medium",
    pace: 87,
    shooting: 80,
    passing: 85,
    dribbling: 88,
    defending: 74,
    physical: 82,
    finishing: 80,
    creativity: 87,
    workRate: 90,
    legacy: 87,
    chemistryTags: ["Verona 1985", "Ala", "Ampiezza"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 29 presenze, 2 gol.",
  },
  "bruni-ver-84": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 75,
    passing: 82,
    dribbling: 79,
    defending: 80,
    physical: 83,
    creativity: 81,
    workRate: 88,
    legacy: 84,
    chemistryTags: ["Verona 1985", "Rotazione", "Mediana"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 27 presenze, 1 gol.",
  },
  "galderisi-ver-84": {
    dataLevel: "curated-high",
    pace: 88,
    shooting: 91,
    passing: 80,
    dribbling: 88,
    physical: 83,
    finishing: 92,
    creativity: 84,
    workRate: 91,
    legacy: 92,
    chemistryTags: ["Verona 1985", "Nanu", "Centravanti"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 29 presenze, 11 gol.",
  },
  "elkjaer-ver-84": {
    dataLevel: "curated-high",
    pace: 90,
    shooting: 93,
    passing: 82,
    dribbling: 91,
    physical: 94,
    finishing: 92,
    creativity: 87,
    workRate: 90,
    legacy: 96,
    chemistryTags: ["Verona 1985", "Elkjaer", "Scarpa persa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 23 presenze, 8 gol.",
  },
  "sacchetti-ver-84": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 75,
    passing: 80,
    dribbling: 77,
    defending: 80,
    physical: 82,
    creativity: 79,
    workRate: 86,
    legacy: 83,
    chemistryTags: ["Verona 1985", "Cambio", "Profondita"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Verona 1984-85: 15 presenze, 1 gol.",
  },
  "lupatelli-chi-01": {
    dataLevel: "curated-medium",
    defending: 86,
    physical: 82,
    passing: 67,
    workRate: 87,
    legacy: 89,
    chemistryTags: ["Chievo 2002", "Numero 10", "Miracolo"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 33 presenze, 33 da titolare.",
  },
  "moro-chi-01": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 72,
    passing: 80,
    dribbling: 78,
    defending: 84,
    physical: 84,
    creativity: 79,
    workRate: 90,
    legacy: 85,
    chemistryTags: ["Chievo 2002", "Fascia", "Provincia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 30 presenze, 1 gol.",
  },
  "lanna-chi-01": {
    dataLevel: "curated-medium",
    pace: 83,
    passing: 81,
    dribbling: 80,
    defending: 84,
    physical: 82,
    creativity: 80,
    workRate: 91,
    legacy: 85,
    chemistryTags: ["Chievo 2002", "Sinistra", "Ampiezza"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 33 presenze, 31 da titolare.",
  },
  "dangelo-chi-01": {
    dataLevel: "curated-medium",
    pace: 73,
    passing: 77,
    defending: 86,
    physical: 88,
    workRate: 88,
    legacy: 87,
    chemistryTags: ["Chievo 2002", "Capitano", "Difesa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 28 presenze, tutte da titolare.",
  },
  "danna-chi-01": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 74,
    passing: 78,
    defending: 86,
    physical: 87,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Chievo 2002", "Linea", "Difesa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 29 presenze, 2 gol.",
  },
  "perrotta-chi-01": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 82,
    passing: 87,
    dribbling: 86,
    defending: 83,
    physical: 87,
    finishing: 81,
    creativity: 88,
    workRate: 95,
    legacy: 90,
    chemistryTags: ["Chievo 2002", "Corsa", "Italia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 32 presenze, 4 gol.",
  },
  "corini-chi-01": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 86,
    passing: 94,
    dribbling: 86,
    defending: 78,
    physical: 81,
    finishing: 85,
    creativity: 96,
    workRate: 90,
    legacy: 91,
    chemistryTags: ["Chievo 2002", "Regista", "Assist"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 30 presenze, 9 gol e 14 assist.",
  },
  "luciano-chi-01": {
    dataLevel: "curated-medium",
    pace: 90,
    shooting: 83,
    passing: 86,
    dribbling: 91,
    defending: 70,
    physical: 82,
    finishing: 82,
    creativity: 89,
    workRate: 89,
    legacy: 88,
    chemistryTags: ["Chievo 2002", "Brasile", "Fascia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 30 presenze, 4 gol.",
  },
  "manfredini-chi-01": {
    dataLevel: "curated-medium",
    pace: 91,
    shooting: 80,
    passing: 84,
    dribbling: 89,
    defending: 72,
    physical: 84,
    finishing: 79,
    creativity: 86,
    workRate: 90,
    legacy: 87,
    chemistryTags: ["Chievo 2002", "Costa d'Avorio", "Fascia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 28 presenze, 2 gol.",
  },
  "corradi-chi-01": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 89,
    passing: 79,
    dribbling: 80,
    physical: 92,
    finishing: 90,
    creativity: 81,
    workRate: 90,
    legacy: 89,
    chemistryTags: ["Chievo 2002", "Torre", "Provincia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 32 presenze, 10 gol.",
  },
  "marazzina-chi-01": {
    dataLevel: "curated-high",
    pace: 83,
    shooting: 91,
    passing: 78,
    dribbling: 84,
    physical: 84,
    finishing: 92,
    creativity: 82,
    workRate: 88,
    legacy: 90,
    chemistryTags: ["Chievo 2002", "Bomber", "Miracolo"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 31 presenze, 13 gol.",
  },
  "cossato-chi-01": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 86,
    passing: 76,
    dribbling: 79,
    physical: 88,
    finishing: 87,
    creativity: 78,
    workRate: 86,
    legacy: 86,
    chemistryTags: ["Chievo 2002", "Cambio", "Gol"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Chievo 2001-02: 32 presenze, 9 gol.",
  },
  "ronaldo-inter-97": {
    dataLevel: "curated-high",
    pace: 99,
    shooting: 98,
    dribbling: 99,
    finishing: 98,
    creativity: 91,
    physical: 90,
    legacy: 100,
    chemistryTags: ["Fenomeno", "Picco irreale", "Brasiliano"],
    sourceNote: "Profilo curato: stagione Inter 1997-98.",
  },
  "maldini-mil-03": {
    dataLevel: "curated-high",
    defending: 99,
    physical: 91,
    passing: 88,
    workRate: 96,
    legacy: 100,
    chemistryTags: ["Capitano", "Milan europeo", "Linea alta"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 30 presenze, tutte da titolare.",
  },
  "baresi-mil-88": {
    dataLevel: "curated-high",
    defending: 99,
    passing: 90,
    physical: 88,
    workRate: 97,
    legacy: 100,
    chemistryTags: ["Libero", "Milan Sacchi", "Capitano"],
    sourceNote: "Profilo curato: Milan 1988-89.",
  },
  "scirea-juv-82": {
    dataLevel: "curated-high",
    defending: 98,
    passing: 91,
    dribbling: 85,
    workRate: 95,
    legacy: 99,
    chemistryTags: ["Libero", "Eleganza", "Juve storica"],
    sourceNote: "Profilo curato: Juventus primi anni 80.",
  },
  "platini-juv-84": {
    dataLevel: "curated-high",
    shooting: 96,
    passing: 97,
    dribbling: 94,
    finishing: 96,
    creativity: 98,
    legacy: 99,
    chemistryTags: ["Le Roi", "Regia gol", "Numero 10"],
    sourceNote: "Profilo curato: Juventus 1984-85.",
  },
  "totti-roma-00": {
    dataLevel: "curated-high",
    shooting: 95,
    passing: 97,
    dribbling: 94,
    finishing: 94,
    creativity: 98,
    physical: 86,
    legacy: 99,
    chemistryTags: ["Capitano", "Roma scudetto", "Trequarti"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 30 presenze, 13 gol in Serie A.",
  },
  "nesta-laz-99": {
    dataLevel: "curated-high",
    defending: 99,
    pace: 86,
    physical: 91,
    passing: 86,
    workRate: 96,
    legacy: 99,
    chemistryTags: ["Capitano Lazio", "Scudetto 2000", "Marcatura"],
    sourceNote: "Profilo curato: Lazio 1999-00.",
  },
  "nesta-mil-03": {
    dataLevel: "curated-high",
    defending: 98,
    pace: 85,
    physical: 90,
    passing: 87,
    workRate: 95,
    legacy: 98,
    chemistryTags: ["Milan europeo", "Eleganza", "Centrale"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 26 presenze, tutte da titolare.",
  },
  "sheva-mil-03": {
    dataLevel: "curated-high",
    pace: 94,
    shooting: 98,
    dribbling: 92,
    finishing: 98,
    workRate: 91,
    legacy: 98,
    chemistryTags: ["Pallone d'Oro", "Milan europeo", "Finalizzatore"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 32 presenze, 24 gol e 3 assist.",
  },
  "kaka-mil-03": {
    dataLevel: "curated-high",
    pace: 94,
    shooting: 91,
    passing: 93,
    dribbling: 97,
    finishing: 90,
    creativity: 96,
    physical: 82,
    workRate: 88,
    legacy: 96,
    chemistryTags: ["Milan scudetto", "Strappo", "Trequarti"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 30 presenze, 10 gol e 5 assist.",
  },
  "dida-mil-03": {
    dataLevel: "curated-high",
    defending: 93,
    physical: 89,
    passing: 74,
    workRate: 91,
    legacy: 92,
    chemistryTags: ["Milan scudetto", "Portiere", "Brasile"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 32 presenze, tutte da titolare.",
  },
  "pirlo-mil-03": {
    dataLevel: "curated-high",
    pace: 72,
    shooting: 88,
    passing: 98,
    dribbling: 90,
    defending: 78,
    physical: 80,
    finishing: 82,
    creativity: 98,
    workRate: 91,
    legacy: 97,
    chemistryTags: ["Milan scudetto", "Regista", "Assist"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 32 presenze, 6 gol e 7 assist.",
  },
  "cafu-mil-03": {
    dataLevel: "curated-high",
    pace: 92,
    shooting: 76,
    passing: 89,
    dribbling: 90,
    defending: 88,
    physical: 88,
    creativity: 88,
    workRate: 94,
    legacy: 94,
    chemistryTags: ["Milan scudetto", "Fascia", "Brasile"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 28 presenze, 1 gol e 5 assist.",
  },
  "seedorf-mil-03": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 86,
    passing: 93,
    dribbling: 92,
    defending: 82,
    physical: 88,
    finishing: 84,
    creativity: 93,
    workRate: 90,
    legacy: 94,
    chemistryTags: ["Milan scudetto", "Mezzala", "Tecnica"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 29 presenze, 3 gol e 6 assist.",
  },
  "gattuso-mil-03": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 72,
    passing: 83,
    dribbling: 78,
    defending: 91,
    physical: 94,
    finishing: 68,
    creativity: 78,
    workRate: 99,
    legacy: 93,
    chemistryTags: ["Milan scudetto", "Ringhio", "Lavoro sporco"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 33 presenze, 29 da titolare.",
  },
  "tomasson-mil-03": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 88,
    passing: 84,
    dribbling: 84,
    physical: 82,
    finishing: 90,
    creativity: 84,
    workRate: 88,
    legacy: 88,
    chemistryTags: ["Milan scudetto", "Jolly", "Rotazione"],
    sourceNote: "Profilo curato con riga StatsCrew Milan 2003-04: 26 presenze, 12 gol e 5 assist.",
  },
  "kaka-mil-06": {
    dataLevel: "curated-high",
    pace: 95,
    shooting: 93,
    passing: 94,
    dribbling: 98,
    finishing: 92,
    creativity: 97,
    legacy: 98,
    chemistryTags: ["Strappo", "Milan Atene", "Trequarti"],
    sourceNote: "Profilo curato: Milan 2006-07.",
  },
  "buffon-juv-01": {
    dataLevel: "curated-high",
    defending: 98,
    physical: 92,
    passing: 78,
    workRate: 98,
    legacy: 99,
    chemistryTags: ["Numero 1", "Clean sheet", "Leadership"],
    sourceNote: "Profilo curato: Juventus 2001-02.",
  },
  "buffon-parma-98": {
    dataLevel: "curated-high",
    defending: 96,
    physical: 91,
    passing: 76,
    workRate: 95,
    legacy: 98,
    chemistryTags: ["Parma coppa", "Portiere", "Buffon giovane"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 34 presenze, tutte da titolare.",
  },
  "vialli-sam-90": {
    dataLevel: "curated-high",
    shooting: 95,
    finishing: 96,
    physical: 91,
    workRate: 94,
    legacy: 97,
    chemistryTags: ["Gemelli del gol", "Samp scudetto", "Leader"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 26 presenze, 19 gol.",
  },
  "mancini-sam-90": {
    dataLevel: "curated-high",
    passing: 94,
    dribbling: 92,
    shooting: 88,
    finishing: 88,
    creativity: 96,
    legacy: 95,
    chemistryTags: ["Gemelli del gol", "Rifinitore", "Samp scudetto"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 30 presenze, 12 gol.",
  },
  "pagliuca-sam-90": {
    dataLevel: "curated-high",
    defending: 94,
    physical: 88,
    passing: 72,
    workRate: 94,
    legacy: 93,
    chemistryTags: ["Samp scudetto", "Portiere", "Marassi"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 32 presenze, tutte da titolare.",
  },
  "vierchowod-sam-90": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 72,
    passing: 78,
    dribbling: 76,
    defending: 96,
    physical: 95,
    creativity: 70,
    workRate: 94,
    legacy: 95,
    chemistryTags: ["Samp scudetto", "Marcatura", "Zar"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 30 presenze, 3 gol.",
  },
  "mannini-sam-90": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 72,
    passing: 82,
    dribbling: 82,
    defending: 88,
    physical: 86,
    creativity: 78,
    workRate: 91,
    legacy: 88,
    chemistryTags: ["Samp scudetto", "Fascia", "Italia"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 26 presenze, 2 gol.",
  },
  "lanna-sam-90": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 60,
    passing: 78,
    dribbling: 76,
    defending: 87,
    physical: 85,
    creativity: 70,
    workRate: 89,
    legacy: 87,
    chemistryTags: ["Samp scudetto", "Linea", "Difesa"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 26 presenze.",
  },
  "katanec-sam-90": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 74,
    passing: 84,
    dribbling: 80,
    defending: 88,
    physical: 90,
    creativity: 80,
    workRate: 91,
    legacy: 88,
    chemistryTags: ["Samp scudetto", "Jolly", "Equilibrio"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 26 presenze, 2 gol.",
  },
  "cerezo-sam-90": {
    dataLevel: "curated-high",
    pace: 72,
    shooting: 82,
    passing: 94,
    dribbling: 88,
    defending: 78,
    physical: 82,
    finishing: 80,
    creativity: 94,
    workRate: 86,
    legacy: 93,
    chemistryTags: ["Samp scudetto", "Regia", "Brasile"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 12 presenze, 3 gol.",
  },
  "lombardo-sam-90": {
    dataLevel: "curated-high",
    pace: 92,
    shooting: 82,
    passing: 86,
    dribbling: 88,
    defending: 78,
    physical: 88,
    finishing: 82,
    creativity: 86,
    workRate: 95,
    legacy: 91,
    chemistryTags: ["Samp scudetto", "Corsa", "Popeye"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 32 presenze, 3 gol.",
  },
  "branca-sam-90": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 84,
    passing: 76,
    dribbling: 80,
    physical: 82,
    finishing: 86,
    creativity: 76,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Samp scudetto", "Rotazione", "Punta"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 20 presenze, 5 gol.",
  },
  "dossena-sam-90": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 76,
    passing: 86,
    dribbling: 82,
    defending: 84,
    physical: 86,
    creativity: 84,
    workRate: 93,
    legacy: 88,
    chemistryTags: ["Samp scudetto", "Equilibrio", "Sempre presente"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 34 presenze, tutte da titolare.",
  },
  "pari-sam-90": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 68,
    passing: 84,
    dribbling: 80,
    defending: 86,
    physical: 86,
    creativity: 80,
    workRate: 92,
    legacy: 86,
    chemistryTags: ["Samp scudetto", "Cerniera", "Duttilita"],
    sourceNote: "Profilo curato con riga StatsCrew Sampdoria 1990-91: 33 presenze, 32 da titolare.",
  },
  "zola-parma-94": {
    dataLevel: "curated-high",
    dribbling: 94,
    passing: 92,
    shooting: 90,
    finishing: 90,
    creativity: 95,
    legacy: 94,
    chemistryTags: ["Punizioni", "Fantasia", "Parma anni 90"],
    sourceNote: "Profilo curato: Parma 1994-95.",
  },
  "thuram-parma-98": {
    dataLevel: "curated-high",
    pace: 90,
    defending: 96,
    physical: 96,
    workRate: 94,
    legacy: 96,
    chemistryTags: ["Parma coppa", "Muro", "Francia"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 34 presenze, 3 assist.",
  },
  "cannavaro-parma-98": {
    dataLevel: "curated-high",
    pace: 88,
    defending: 95,
    physical: 90,
    workRate: 95,
    legacy: 95,
    chemistryTags: ["Parma coppa", "Marcatura", "Italia"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 30 presenze, 1 gol.",
  },
  "crespo-parma-98": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 94,
    passing: 80,
    dribbling: 88,
    physical: 88,
    finishing: 95,
    creativity: 82,
    workRate: 88,
    legacy: 94,
    chemistryTags: ["Parma coppa", "Finalizzatore", "Argentina"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 30 presenze, 16 gol e 3 assist.",
  },
  "benarrivo-parma-98": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 64,
    passing: 82,
    dribbling: 84,
    defending: 88,
    physical: 84,
    creativity: 78,
    workRate: 91,
    legacy: 88,
    chemistryTags: ["Parma coppa", "Fascia", "Italia"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 25 presenze, tutte da titolare.",
  },
  "sensini-parma-98": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 72,
    passing: 86,
    dribbling: 80,
    defending: 90,
    physical: 89,
    creativity: 82,
    workRate: 91,
    legacy: 90,
    chemistryTags: ["Parma coppa", "Jolly", "Argentina"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 26 presenze, 1 gol e 1 assist.",
  },
  "dinobaggio-parma-98": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 82,
    passing: 86,
    dribbling: 82,
    defending: 84,
    physical: 90,
    finishing: 80,
    creativity: 84,
    workRate: 92,
    legacy: 90,
    chemistryTags: ["Parma coppa", "Inserimento", "Italia"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 29 presenze, 2 gol e 3 assist.",
  },
  "veron-parma-98": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 84,
    passing: 95,
    dribbling: 90,
    defending: 78,
    physical: 84,
    finishing: 78,
    creativity: 96,
    workRate: 88,
    legacy: 93,
    chemistryTags: ["Parma coppa", "Brujita", "Assist"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 26 presenze, 1 gol e 8 assist.",
  },
  "chiesa-parma-98": {
    dataLevel: "curated-high",
    pace: 88,
    shooting: 91,
    passing: 86,
    dribbling: 89,
    physical: 82,
    finishing: 91,
    creativity: 88,
    workRate: 88,
    legacy: 91,
    chemistryTags: ["Parma coppa", "Seconda punta", "Assist"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 30 presenze, 9 gol e 10 assist.",
  },
  "fuser-parma-98": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 86,
    passing: 90,
    dribbling: 86,
    defending: 78,
    physical: 85,
    finishing: 84,
    creativity: 90,
    workRate: 92,
    legacy: 89,
    chemistryTags: ["Parma coppa", "Fascia", "Assist"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 32 presenze, 8 gol e 7 assist.",
  },
  "stanic-parma-98": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 84,
    passing: 80,
    dribbling: 84,
    physical: 86,
    finishing: 85,
    creativity: 80,
    workRate: 86,
    legacy: 86,
    chemistryTags: ["Parma coppa", "Rotazione", "Croazia"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 18 presenze, 7 gol.",
  },
  "fiore-parma-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 74,
    passing: 86,
    dribbling: 84,
    defending: 78,
    physical: 82,
    creativity: 86,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Parma coppa", "Mediana", "Italia"],
    sourceNote: "Profilo curato con riga StatsCrew Parma 1998-99: 28 presenze.",
  },
  "delpiero-juv-97": {
    dataLevel: "curated-high",
    shooting: 96,
    passing: 91,
    dribbling: 95,
    finishing: 96,
    creativity: 94,
    legacy: 98,
    chemistryTags: ["Pinturicchio", "Juve anni 90", "Gol a giro"],
    sourceNote: "Profilo curato: Juventus 1997-98.",
  },
  "batistuta-fio-98": {
    dataLevel: "curated-high",
    shooting: 98,
    finishing: 98,
    physical: 93,
    workRate: 88,
    legacy: 97,
    chemistryTags: ["Batigol", "Fiorentina", "Cannonata"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Fiorentina 1998-99: 28 presenze, 21 gol.",
  },
  "batistuta-roma-00": {
    dataLevel: "curated-high",
    shooting: 95,
    finishing: 96,
    physical: 90,
    workRate: 86,
    legacy: 95,
    chemistryTags: ["Roma scudetto", "Batigol", "Finalizzatore"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 28 presenze, 20 gol in Serie A.",
  },
  "hamsik-nap-12": {
    dataLevel: "curated-high",
    passing: 92,
    shooting: 89,
    dribbling: 90,
    creativity: 92,
    workRate: 92,
    legacy: 94,
    chemistryTags: ["Bandiera Napoli", "Inserimento", "Cresta"],
    sourceNote: "Profilo curato: Napoli 2012-13.",
  },
  "cavani-nap-12": {
    dataLevel: "curated-high",
    pace: 91,
    shooting: 95,
    finishing: 96,
    physical: 90,
    workRate: 96,
    legacy: 94,
    chemistryTags: ["Matador", "Pressing", "Napoli moderno"],
    sourceNote: "Profilo curato: Napoli 2012-13.",
  },
  "immobile-laz-19": {
    dataLevel: "curated-high",
    shooting: 97,
    finishing: 98,
    physical: 86,
    workRate: 88,
    legacy: 94,
    chemistryTags: ["Scarpa d'Oro", "Lazio moderna", "Area"],
    sourceNote: "Profilo curato: Lazio 2019-20.",
  },
  "kvara-nap-22": {
    dataLevel: "curated-high",
    pace: 92,
    shooting: 90,
    passing: 90,
    dribbling: 96,
    finishing: 90,
    creativity: 95,
    legacy: 94,
    chemistryTags: ["Kvaradona", "Napoli scudetto", "Uno contro uno"],
    sourceNote: "Profilo curato: Napoli 2022-23.",
  },
  "osimhen-nap-22": {
    dataLevel: "curated-high",
    pace: 95,
    shooting: 95,
    finishing: 96,
    physical: 94,
    workRate: 92,
    legacy: 95,
    chemistryTags: ["Maschera", "Napoli scudetto", "Profondita"],
    sourceNote: "Profilo curato: Napoli 2022-23.",
  },
  "pogba-juv-15": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 90,
    passing: 94,
    dribbling: 93,
    defending: 82,
    physical: 92,
    finishing: 88,
    creativity: 95,
    workRate: 91,
    legacy: 94,
    chemistryTags: ["Juve 2015", "Octopus", "Creatore"],
    sourceNote: "Profilo curato con righe JuveFC/TopScorers 2015-16: 8 gol e 12 assist.",
  },
  "dybala-juv-15": {
    dataLevel: "curated-high",
    pace: 90,
    shooting: 93,
    passing: 91,
    dribbling: 96,
    defending: 58,
    physical: 76,
    finishing: 94,
    creativity: 94,
    workRate: 86,
    legacy: 93,
    chemistryTags: ["Juve 2015", "Joya", "Mancino"],
    sourceNote: "Profilo curato con righe JuveFC/TopScorers 2015-16: 19 gol e 9 assist.",
  },
  "mandzukic-juv-15": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 90,
    passing: 80,
    dribbling: 80,
    defending: 72,
    physical: 94,
    finishing: 91,
    creativity: 78,
    workRate: 96,
    legacy: 90,
    chemistryTags: ["Juve 2015", "Pressing", "Area"],
    sourceNote: "Profilo curato con riga JuveFC 2015-16.",
  },
  "barzagli-juv-15": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 48,
    passing: 78,
    dribbling: 72,
    defending: 95,
    physical: 89,
    finishing: 45,
    creativity: 72,
    workRate: 94,
    legacy: 92,
    chemistryTags: ["Juve 2015", "BBC", "Marcatura"],
    sourceNote: "Profilo curato con riga JuveFC 2015-16.",
  },
  "marchisio-juv-15": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 82,
    passing: 90,
    dribbling: 86,
    defending: 84,
    physical: 83,
    finishing: 80,
    creativity: 90,
    workRate: 92,
    legacy: 90,
    chemistryTags: ["Juve 2015", "Principino", "Regia"],
    sourceNote: "Profilo curato con riga JuveFC 2015-16.",
  },
  "alexsandro-juv-15": {
    dataLevel: "curated-medium",
    pace: 89,
    shooting: 72,
    passing: 84,
    dribbling: 86,
    defending: 86,
    physical: 86,
    finishing: 68,
    creativity: 84,
    workRate: 90,
    legacy: 88,
    chemistryTags: ["Juve 2015", "Fascia", "Brasile"],
    sourceNote: "Profilo curato con riga JuveFC 2015-16.",
  },
  "leao-mil-21": {
    dataLevel: "curated-high",
    pace: 96,
    shooting: 91,
    dribbling: 95,
    finishing: 90,
    creativity: 90,
    legacy: 93,
    chemistryTags: ["Strappo", "Milan scudetto", "Ala"],
    sourceNote: "Profilo curato: Milan 2021-22.",
  },
  "zanetti-inter-09": {
    dataLevel: "curated-high",
    pace: 88,
    defending: 94,
    passing: 89,
    dribbling: 88,
    physical: 93,
    workRate: 98,
    legacy: 98,
    chemistryTags: ["Capitano", "Zanetti", "Triplete"],
    sourceNote: "Profilo curato: leader storico dell'Inter del Triplete 2009-10.",
  },
  "sneijder-inter-09": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 92,
    passing: 96,
    dribbling: 90,
    creativity: 97,
    physical: 76,
    legacy: 97,
    chemistryTags: ["Triplete", "Rifinitore", "Numero 10"],
    sourceNote: "Profilo curato: regista offensivo Inter 2009-10.",
  },
  "juliocesar-inter-09": {
    dataLevel: "curated-high",
    defending: 95,
    physical: 88,
    passing: 74,
    workRate: 94,
    legacy: 95,
    chemistryTags: ["Triplete", "Portiere", "Brasile"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10.",
  },
  "maicon-inter-09": {
    dataLevel: "curated-high",
    pace: 92,
    shooting: 84,
    passing: 90,
    dribbling: 90,
    defending: 91,
    physical: 93,
    creativity: 88,
    workRate: 94,
    legacy: 95,
    chemistryTags: ["Triplete", "Colosso", "Brasile"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10.",
  },
  "milito-inter-09": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 96,
    passing: 82,
    dribbling: 90,
    physical: 87,
    finishing: 97,
    creativity: 86,
    workRate: 89,
    legacy: 96,
    chemistryTags: ["Triplete", "Principe", "Finale"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10: 35 presenze, 22 gol in Serie A.",
  },
  "etoo-inter-09": {
    dataLevel: "curated-high",
    pace: 91,
    shooting: 92,
    passing: 86,
    dribbling: 91,
    defending: 74,
    physical: 86,
    finishing: 93,
    creativity: 88,
    workRate: 96,
    legacy: 95,
    chemistryTags: ["Triplete", "Sacrificio", "Camerun"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10: 32 presenze, 12 gol in Serie A.",
  },
  "lucio-inter-09": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 66,
    passing: 82,
    dribbling: 80,
    defending: 94,
    physical: 94,
    creativity: 78,
    workRate: 93,
    legacy: 93,
    chemistryTags: ["Triplete", "Conduzione", "Brasile"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10.",
  },
  "samuel-inter-09": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 66,
    passing: 76,
    dribbling: 72,
    defending: 95,
    physical: 93,
    creativity: 70,
    workRate: 93,
    legacy: 93,
    chemistryTags: ["Triplete", "Muro", "Argentina"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10.",
  },
  "cambiasso-inter-09": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 82,
    passing: 91,
    dribbling: 85,
    defending: 90,
    physical: 88,
    finishing: 80,
    creativity: 90,
    workRate: 96,
    legacy: 93,
    chemistryTags: ["Triplete", "Equilibrio", "Argentina"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10.",
  },
  "pandev-inter-09": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 84,
    passing: 86,
    dribbling: 88,
    defending: 58,
    physical: 78,
    finishing: 84,
    creativity: 88,
    workRate: 86,
    legacy: 88,
    chemistryTags: ["Triplete", "Jolly", "Gennaio"],
    sourceNote: "Profilo curato con riga worldfootball.net/Inter ufficiale 2009-10.",
  },
  "cafu-roma-00": {
    dataLevel: "curated-high",
    pace: 93,
    defending: 88,
    passing: 88,
    dribbling: 90,
    physical: 90,
    workRate: 95,
    legacy: 97,
    chemistryTags: ["Pendolino", "Roma scudetto", "Corsa"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01.",
  },
  "tommasi-roma-00": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 76,
    passing: 88,
    dribbling: 82,
    defending: 89,
    physical: 90,
    creativity: 84,
    workRate: 97,
    legacy: 91,
    chemistryTags: ["Roma scudetto", "Equilibrio", "Lavoro sporco"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 34 presenze, 31 da titolare.",
  },
  "delvecchio-roma-00": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 84,
    passing: 80,
    dribbling: 82,
    defending: 70,
    physical: 88,
    finishing: 84,
    creativity: 80,
    workRate: 92,
    legacy: 88,
    chemistryTags: ["Roma scudetto", "Sacrificio", "Derby"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 31 presenze in Serie A.",
  },
  "zago-roma-00": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 62,
    passing: 78,
    dribbling: 72,
    defending: 89,
    physical: 90,
    creativity: 68,
    workRate: 88,
    legacy: 87,
    chemistryTags: ["Roma scudetto", "Brasile", "Marcatura"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 28 presenze, tutte da titolare.",
  },
  "emerson-roma-00": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 78,
    passing: 88,
    dribbling: 84,
    defending: 84,
    physical: 91,
    creativity: 86,
    workRate: 89,
    legacy: 89,
    chemistryTags: ["Roma scudetto", "Brasile", "Regia fisica"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 13 presenze, 3 gol in Serie A.",
  },
  "veron-laz-99": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 91,
    passing: 96,
    dribbling: 92,
    creativity: 96,
    physical: 84,
    legacy: 96,
    chemistryTags: ["Lazio scudetto", "Regia", "Brujita"],
    sourceNote: "Profilo curato: centrocampista totale Lazio scudetto 1999-00.",
  },
  "marchegiani-laz-99": {
    dataLevel: "curated-high",
    defending: 91,
    physical: 86,
    passing: 70,
    workRate: 91,
    legacy: 90,
    chemistryTags: ["Lazio scudetto", "Portiere", "Marchegiani"],
    sourceNote: "Profilo curato con stat line Lazio Stories 1999-00: 28 presenze in Serie A.",
  },
  "mihajlovic-laz-99": {
    dataLevel: "curated-high",
    shooting: 89,
    passing: 91,
    defending: 90,
    physical: 88,
    creativity: 88,
    workRate: 90,
    legacy: 92,
    chemistryTags: ["Lazio scudetto", "Punizioni", "Mancino"],
    sourceNote: "Profilo curato con stat line Lazio Stories 1999-00.",
  },
  "stam-laz-01": {
    dataLevel: "curated-high",
    pace: 84,
    defending: 95,
    physical: 98,
    passing: 78,
    workRate: 92,
    legacy: 93,
    chemistryTags: ["Lazio", "Fisico", "Marcatura"],
    sourceNote: "Profilo curato: centrale dominante della Lazio primi anni 2000.",
  },
  "simeone-laz-99": {
    dataLevel: "curated-high",
    shooting: 84,
    passing: 88,
    dribbling: 86,
    defending: 91,
    physical: 92,
    workRate: 97,
    legacy: 92,
    chemistryTags: ["Lazio scudetto", "Garra", "Pressing"],
    sourceNote: "Profilo curato con stat line Lazio Stories 1999-00.",
  },
  "signori-laz-92": {
    dataLevel: "curated-high",
    pace: 90,
    shooting: 96,
    passing: 86,
    dribbling: 91,
    finishing: 97,
    creativity: 90,
    legacy: 95,
    chemistryTags: ["Beppe-gol", "Zeman", "Rigori senza rincorsa"],
    sourceNote: "Profilo curato con riga RSSSF 1992-93: 32 presenze, 26 gol.",
  },
  "salas-laz-99": {
    dataLevel: "curated-high",
    pace: 87,
    shooting: 93,
    passing: 82,
    dribbling: 88,
    physical: 90,
    finishing: 94,
    legacy: 92,
    chemistryTags: ["Lazio scudetto", "Matador", "Cile"],
    sourceNote: "Profilo curato con stat line Lazio Stories 1999-00.",
  },
  "klose-laz-11": {
    dataLevel: "curated-high",
    shooting: 91,
    passing: 84,
    dribbling: 84,
    physical: 88,
    finishing: 95,
    creativity: 86,
    workRate: 91,
    legacy: 91,
    chemistryTags: ["Lazio", "Movimento", "Colpo di testa"],
    sourceNote: "Profilo curato con riga ESPN 2011-12.",
  },
  "quagliarella-sam-18": {
    dataLevel: "curated-high",
    shooting: 94,
    finishing: 95,
    passing: 86,
    creativity: 88,
    legacy: 92,
    chemistryTags: ["Gol impossibili", "Samp moderna", "Cult"],
    sourceNote: "Profilo curato: Sampdoria 2018-19.",
  },
  "toldo-fio-98": {
    dataLevel: "curated-high",
    defending: 94,
    physical: 88,
    passing: 72,
    workRate: 93,
    legacy: 93,
    chemistryTags: ["Fiorentina", "Guanti viola", "Provincia"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Fiorentina 1998-99: 33 presenze, tutte da titolare.",
  },
  "torricelli-fio-98": {
    dataLevel: "curated-medium",
    pace: 78,
    passing: 78,
    defending: 88,
    physical: 88,
    workRate: 92,
    legacy: 88,
    chemistryTags: ["Fiorentina 98", "Terzino", "Lavoro"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 31 presenze, 2 gol.",
  },
  "heinrich-fio-98": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 80,
    passing: 84,
    dribbling: 82,
    defending: 85,
    physical: 87,
    creativity: 84,
    workRate: 93,
    legacy: 88,
    chemistryTags: ["Fiorentina 98", "Fascia", "Germania"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 33 presenze, 3 gol.",
  },
  "repka-fio-98": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 72,
    defending: 89,
    physical: 92,
    workRate: 89,
    legacy: 88,
    chemistryTags: ["Fiorentina 98", "Marcatura", "Ruvido"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 31 presenze.",
  },
  "padalino-fio-98": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 75,
    passing: 76,
    defending: 87,
    physical: 86,
    workRate: 89,
    legacy: 87,
    chemistryTags: ["Fiorentina 98", "Difesa", "Italia"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 28 presenze, 3 gol.",
  },
  "ruicosta-fio-98": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 88,
    passing: 97,
    dribbling: 94,
    finishing: 86,
    creativity: 98,
    workRate: 88,
    legacy: 95,
    chemistryTags: ["Fiorentina 98", "Maestro", "Portogallo"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Fiorentina 1998-99: 31 presenze, 10 gol.",
  },
  "amoroso-fio-98": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 74,
    passing: 84,
    dribbling: 80,
    defending: 82,
    physical: 85,
    creativity: 82,
    workRate: 90,
    legacy: 85,
    chemistryTags: ["Fiorentina 98", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 28 presenze.",
  },
  "cois-fio-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 72,
    passing: 82,
    dribbling: 79,
    defending: 82,
    physical: 86,
    creativity: 80,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Fiorentina 98", "Polmoni", "Italia"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 23 presenze.",
  },
  "edmundo-fio-98": {
    dataLevel: "curated-high",
    pace: 87,
    shooting: 90,
    passing: 87,
    dribbling: 95,
    finishing: 89,
    creativity: 93,
    workRate: 80,
    legacy: 91,
    chemistryTags: ["Fiorentina 98", "Brasile", "Caos creativo"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Fiorentina 1998-99: 28 presenze, 8 gol.",
  },
  "oliveira-fio-98": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 84,
    passing: 80,
    dribbling: 85,
    physical: 82,
    finishing: 84,
    creativity: 82,
    workRate: 85,
    legacy: 86,
    chemistryTags: ["Fiorentina 98", "Belgio", "Tridente"],
    sourceNote: "Profilo curato con riga BDFutbol Fiorentina 1998-99: 30 presenze, 2 gol.",
  },
  "frey-parma-01": {
    dataLevel: "curated-medium",
    defending: 90,
    physical: 87,
    passing: 70,
    workRate: 90,
    legacy: 90,
    chemistryTags: ["Parma", "Riflessi", "Provincia"],
    sourceNote: "Profilo curato: Parma 2001-02.",
  },
  "handanovic-udi-10": {
    dataLevel: "curated-high",
    defending: 92,
    physical: 88,
    passing: 73,
    workRate: 92,
    legacy: 92,
    chemistryTags: ["Udinese", "Para-rigori", "Modern hard"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Udinese 2010-11: 35 presenze, tutte da titolare.",
  },
  "dinatale-udi-10": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 96,
    passing: 88,
    dribbling: 91,
    finishing: 97,
    creativity: 91,
    workRate: 88,
    legacy: 94,
    chemistryTags: ["Udinese 2010", "Totogoal", "Modern hard"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Udinese 2010-11: 36 presenze, 28 gol e 6 assist.",
  },
  "sanchez-udi-10": {
    dataLevel: "curated-high",
    pace: 92,
    shooting: 90,
    passing: 89,
    dribbling: 95,
    finishing: 90,
    creativity: 94,
    workRate: 90,
    legacy: 92,
    chemistryTags: ["Udinese 2010", "Nino maravilla", "Cile"],
    sourceNote: "Profilo curato con righe StatsCrew/BDFutbol Udinese 2010-11: 31 presenze, 12 gol.",
  },
  "benatia-udi-10": {
    dataLevel: "curated-medium",
    pace: 80,
    passing: 76,
    defending: 89,
    physical: 89,
    workRate: 90,
    legacy: 88,
    chemistryTags: ["Udinese 2010", "Scouting", "Difesa a tre"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 34 presenze, 3 gol.",
  },
  "zapata-udi-10": {
    dataLevel: "curated-medium",
    pace: 81,
    passing: 72,
    defending: 88,
    physical: 90,
    workRate: 90,
    legacy: 87,
    chemistryTags: ["Udinese 2010", "Colombia", "Difesa a tre"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 35 presenze, 2 gol.",
  },
  "domizzi-udi-10": {
    dataLevel: "curated-medium",
    pace: 73,
    passing: 78,
    defending: 86,
    physical: 86,
    workRate: 87,
    legacy: 85,
    chemistryTags: ["Udinese 2010", "Braccetto", "Esperienza"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 30 presenze, 2 gol.",
  },
  "isla-udi-10": {
    dataLevel: "curated-medium",
    pace: 89,
    shooting: 77,
    passing: 84,
    dribbling: 86,
    defending: 83,
    physical: 84,
    creativity: 85,
    workRate: 93,
    legacy: 87,
    chemistryTags: ["Udinese 2010", "Fascia", "Cile"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 34 presenze, 2 gol.",
  },
  "armero-udi-10": {
    dataLevel: "curated-medium",
    pace: 91,
    shooting: 76,
    passing: 82,
    dribbling: 86,
    defending: 80,
    physical: 84,
    creativity: 83,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Udinese 2010", "Fascia", "Colombia"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 31 presenze, 2 gol.",
  },
  "asamoah-udi-10": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 78,
    passing: 86,
    dribbling: 87,
    defending: 84,
    physical: 90,
    creativity: 85,
    workRate: 94,
    legacy: 88,
    chemistryTags: ["Udinese 2010", "Motore", "Ghana"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 38 presenze, 2 gol.",
  },
  "inler-udi-10": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 84,
    passing: 88,
    dribbling: 82,
    defending: 84,
    physical: 87,
    creativity: 86,
    workRate: 91,
    legacy: 87,
    chemistryTags: ["Udinese 2010", "Regia", "Svizzera"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 35 presenze, 3 gol.",
  },
  "pinzi-udi-10": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 74,
    passing: 82,
    dribbling: 80,
    defending: 83,
    physical: 86,
    creativity: 80,
    workRate: 92,
    legacy: 85,
    chemistryTags: ["Udinese 2010", "Pressing", "Italia"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 34 presenze, 1 gol.",
  },
  "denis-udi-10": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 83,
    passing: 75,
    dribbling: 78,
    physical: 89,
    finishing: 84,
    workRate: 83,
    legacy: 84,
    chemistryTags: ["Udinese 2010", "Tanque", "Argentina"],
    sourceNote: "Profilo curato con riga BDFutbol Udinese 2010-11: 25 presenze, 4 gol.",
  },
  "sirigu-pal-09": {
    dataLevel: "curated-medium",
    defending: 89,
    physical: 86,
    passing: 70,
    workRate: 88,
    legacy: 88,
    chemistryTags: ["Palermo 2009", "Portiere", "Barbera"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 32 presenze, tutte da titolare.",
  },
  "cassani-pal-09": {
    dataLevel: "curated-medium",
    pace: 82,
    passing: 80,
    defending: 86,
    physical: 84,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Palermo 2009", "Fascia", "Italia"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 37 presenze, tutte da titolare.",
  },
  "balzaretti-pal-09": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 74,
    passing: 83,
    dribbling: 81,
    defending: 86,
    physical: 84,
    creativity: 82,
    workRate: 92,
    legacy: 87,
    chemistryTags: ["Palermo 2009", "Fascia", "Sinistra"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 34 presenze, 1 gol.",
  },
  "kjaer-pal-09": {
    dataLevel: "curated-medium",
    pace: 78,
    passing: 74,
    defending: 88,
    physical: 89,
    workRate: 89,
    legacy: 88,
    chemistryTags: ["Palermo 2009", "Scouting", "Danimarca"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 35 presenze, 2 gol.",
  },
  "bovo-pal-09": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 78,
    passing: 76,
    defending: 85,
    physical: 86,
    workRate: 86,
    legacy: 85,
    chemistryTags: ["Palermo 2009", "Piazzati", "Difesa"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 29 presenze, 2 gol.",
  },
  "nocerino-pal-09": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 78,
    passing: 82,
    dribbling: 80,
    defending: 82,
    physical: 88,
    creativity: 80,
    workRate: 94,
    legacy: 86,
    chemistryTags: ["Palermo 2009", "Corsa", "Italia"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 35 presenze, 2 gol.",
  },
  "migliaccio-pal-09": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 72,
    passing: 78,
    dribbling: 76,
    defending: 84,
    physical: 88,
    creativity: 77,
    workRate: 91,
    legacy: 85,
    chemistryTags: ["Palermo 2009", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 33 presenze.",
  },
  "simplicio-pal-09": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 83,
    passing: 87,
    dribbling: 85,
    defending: 76,
    physical: 83,
    finishing: 82,
    creativity: 88,
    workRate: 86,
    legacy: 87,
    chemistryTags: ["Palermo 2009", "Brasile", "Inserimenti"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 28 presenze, 3 gol.",
  },
  "pastore-pal-09": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 84,
    passing: 91,
    dribbling: 94,
    finishing: 82,
    creativity: 94,
    workRate: 82,
    legacy: 91,
    chemistryTags: ["Palermo 2009", "El Flaco", "Argentina"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 34 presenze, 3 gol.",
  },
  "miccoli-pal-09": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 94,
    passing: 90,
    dribbling: 94,
    finishing: 94,
    creativity: 94,
    workRate: 86,
    legacy: 93,
    chemistryTags: ["Palermo 2009", "Punizioni", "Barbera"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 35 presenze, 19 gol.",
  },
  "cavani-pal-09": {
    dataLevel: "curated-high",
    pace: 88,
    shooting: 90,
    passing: 78,
    dribbling: 85,
    physical: 89,
    finishing: 91,
    workRate: 92,
    legacy: 91,
    chemistryTags: ["Palermo 2009", "Matador", "Uruguay"],
    sourceNote: "Profilo curato con riga BDFutbol Palermo 2009-10: 34 presenze, 13 gol.",
  },
  "miccoli-pal-11": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 92,
    passing: 91,
    dribbling: 94,
    finishing: 92,
    creativity: 95,
    legacy: 92,
    chemistryTags: ["Palermo", "Punizioni", "Provincia"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 16 gol e 12 assist.",
  },
  "giovinco-parma-11": {
    dataLevel: "curated-high",
    pace: 91,
    shooting: 90,
    passing: 91,
    dribbling: 94,
    finishing: 90,
    creativity: 94,
    legacy: 91,
    chemistryTags: ["Parma", "Formica atomica", "Provincia"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 15 gol e 11 assist.",
  },
  "denis-ata-11": {
    dataLevel: "curated-medium",
    shooting: 90,
    passing: 76,
    dribbling: 80,
    physical: 93,
    finishing: 92,
    workRate: 87,
    legacy: 89,
    chemistryTags: ["Atalanta", "Tanque", "Modern hard"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 16 gol.",
  },
  "jovetic-fio-11": {
    dataLevel: "curated-high",
    pace: 87,
    shooting: 90,
    passing: 87,
    dribbling: 93,
    finishing: 90,
    creativity: 92,
    legacy: 91,
    chemistryTags: ["Fiorentina", "Jo-Jo", "Trequarti"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 14 gol.",
  },
  "divaio-bol-11": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 88,
    passing: 82,
    dribbling: 83,
    finishing: 90,
    creativity: 84,
    workRate: 88,
    legacy: 88,
    chemistryTags: ["Bologna", "Capitano", "Provincia"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 10 gol e 6 assist.",
  },
  "diamanti-bol-11": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 87,
    passing: 90,
    dribbling: 89,
    finishing: 86,
    creativity: 92,
    workRate: 87,
    legacy: 88,
    chemistryTags: ["Bologna", "Mancino", "Punizioni"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 7 gol e 6 assist.",
  },
  "sterchele-bol-97": {
    dataLevel: "curated-medium",
    defending: 85,
    physical: 84,
    passing: 68,
    workRate: 86,
    legacy: 84,
    chemistryTags: ["Bologna 97", "Portiere", "Dall'Ara"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 32 presenze, tutte da titolare.",
  },
  "paramatti-bol-97": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 80,
    passing: 84,
    dribbling: 81,
    defending: 86,
    physical: 85,
    creativity: 84,
    workRate: 92,
    legacy: 87,
    chemistryTags: ["Bologna 97", "Fascia", "Goleador"],
    sourceNote: "Profilo curato con righe BDFutbol/Transfermarkt Bologna 1997-98: 28 presenze, 5 gol e 3 assist.",
  },
  "mangone-bol-97": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 72,
    defending: 85,
    physical: 87,
    workRate: 87,
    legacy: 85,
    chemistryTags: ["Bologna 97", "Difesa", "Ulivieri"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 32 presenze, 30 da titolare.",
  },
  "paganin-bol-97": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 72,
    defending: 85,
    physical: 87,
    workRate: 86,
    legacy: 85,
    chemistryTags: ["Bologna 97", "Esperienza", "Difesa"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 31 presenze, 1 gol.",
  },
  "torrisi-bol-97": {
    dataLevel: "curated-medium",
    pace: 73,
    passing: 76,
    defending: 84,
    physical: 86,
    creativity: 77,
    workRate: 85,
    legacy: 84,
    chemistryTags: ["Bologna 97", "Marcatura", "Assist"],
    sourceNote: "Profilo curato con righe BDFutbol/Transfermarkt Bologna 1997-98: 24 presenze e 3 assist.",
  },
  "marocchi-bol-97": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 76,
    passing: 86,
    dribbling: 81,
    defending: 82,
    physical: 84,
    creativity: 84,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Bologna 97", "Regia", "Veterano"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 31 presenze, 1 gol.",
  },
  "magoni-bol-97": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 70,
    passing: 80,
    dribbling: 78,
    defending: 82,
    physical: 86,
    creativity: 78,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Bologna 97", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 32 presenze, 30 da titolare.",
  },
  "nervo-bol-97": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 77,
    passing: 83,
    dribbling: 85,
    defending: 76,
    physical: 82,
    creativity: 84,
    workRate: 91,
    legacy: 86,
    chemistryTags: ["Bologna 97", "Fascia", "Strappi"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 33 presenze, 1 gol.",
  },
  "andersson-bol-97": {
    dataLevel: "curated-high",
    pace: 74,
    shooting: 88,
    passing: 84,
    dribbling: 78,
    physical: 94,
    finishing: 90,
    creativity: 86,
    workRate: 87,
    legacy: 90,
    chemistryTags: ["Bologna 97", "Torre", "Svezia"],
    sourceNote: "Profilo curato con righe BDFutbol/Transfermarkt Bologna 1997-98: 32 presenze, 12 gol e 7 assist.",
  },
  "baggio-bol-97": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 96,
    passing: 96,
    dribbling: 98,
    physical: 78,
    finishing: 97,
    creativity: 99,
    workRate: 84,
    legacy: 98,
    chemistryTags: ["Bologna 97", "Divin Codino", "Dall'Ara"],
    sourceNote: "Profilo curato con righe BDFutbol/Transfermarkt Bologna 1997-98: 30 presenze, 22 gol e 9 assist.",
  },
  "kolyvanov-bol-97": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 88,
    passing: 84,
    dribbling: 88,
    physical: 82,
    finishing: 88,
    creativity: 87,
    workRate: 85,
    legacy: 88,
    chemistryTags: ["Bologna 97", "Russia", "Sinistro"],
    sourceNote: "Profilo curato con righe BDFutbol/Transfermarkt Bologna 1997-98: 31 presenze, 9 gol e 4 assist.",
  },
  "fontolan-bol-97": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 80,
    passing: 80,
    dribbling: 83,
    finishing: 80,
    creativity: 81,
    workRate: 83,
    legacy: 84,
    chemistryTags: ["Bologna 97", "Cambio", "Cult"],
    sourceNote: "Profilo curato con riga BDFutbol Bologna 1997-98: 27 presenze, 2 gol.",
  },
  "moralez-ata-11": {
    dataLevel: "curated-medium",
    pace: 88,
    shooting: 84,
    passing: 86,
    dribbling: 91,
    finishing: 84,
    creativity: 89,
    workRate: 86,
    legacy: 87,
    chemistryTags: ["Atalanta", "Frasquito", "Argentina"],
    sourceNote: "Profilo curato con riga ESPN 2011-12: 6 gol e 5 assist.",
  },
  "gollini-ata-18": {
    dataLevel: "curated-medium",
    defending: 86,
    physical: 84,
    passing: 72,
    workRate: 86,
    legacy: 86,
    chemistryTags: ["Atalanta 2018", "Portiere", "Champions"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 20 presenze, tutte da titolare.",
  },
  "hateboer-ata-18": {
    dataLevel: "curated-medium",
    pace: 88,
    shooting: 81,
    passing: 83,
    dribbling: 82,
    defending: 84,
    physical: 88,
    creativity: 83,
    workRate: 94,
    legacy: 87,
    chemistryTags: ["Atalanta 2018", "Quinto", "Pressing"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 35 presenze, 5 gol.",
  },
  "gosens-ata-18": {
    dataLevel: "curated-medium",
    pace: 89,
    shooting: 80,
    passing: 84,
    dribbling: 84,
    defending: 83,
    physical: 87,
    creativity: 84,
    workRate: 93,
    legacy: 88,
    chemistryTags: ["Atalanta 2018", "Fascia sinistra", "Germania"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 28 presenze, 3 gol.",
  },
  "mancini-ata-18": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 80,
    passing: 76,
    defending: 87,
    physical: 89,
    creativity: 76,
    workRate: 89,
    legacy: 87,
    chemistryTags: ["Atalanta 2018", "Braccetto", "Goleador"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 30 presenze, 5 gol.",
  },
  "palomino-ata-18": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 74,
    defending: 86,
    physical: 90,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Atalanta 2018", "Duelli", "Argentina"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 30 presenze, 1 gol.",
  },
  "freuler-ata-18": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 78,
    passing: 88,
    dribbling: 84,
    defending: 84,
    physical: 86,
    creativity: 86,
    workRate: 94,
    legacy: 88,
    chemistryTags: ["Atalanta 2018", "Regia", "Svizzera"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 35 presenze, 2 gol.",
  },
  "papugomez-ata-18": {
    dataLevel: "curated-high",
    pace: 87,
    shooting: 88,
    passing: 94,
    dribbling: 95,
    finishing: 86,
    creativity: 96,
    workRate: 91,
    legacy: 92,
    chemistryTags: ["Atalanta 2018", "Papu", "Capitano"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 35 presenze, 7 gol.",
  },
  "deroon-ata-18": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 76,
    passing: 84,
    dribbling: 80,
    defending: 86,
    physical: 89,
    creativity: 82,
    workRate: 95,
    legacy: 87,
    chemistryTags: ["Atalanta 2018", "Pressing", "Paesi Bassi"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 35 presenze, 2 gol.",
  },
  "pasalic-ata-18": {
    dataLevel: "curated-medium",
    pace: 79,
    shooting: 84,
    passing: 84,
    dribbling: 83,
    defending: 78,
    physical: 86,
    finishing: 84,
    creativity: 84,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Atalanta 2018", "Inserimenti", "Croazia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 33 presenze, 5 gol.",
  },
  "zapata-ata-18": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 94,
    passing: 78,
    dribbling: 85,
    physical: 96,
    finishing: 95,
    creativity: 82,
    workRate: 89,
    legacy: 93,
    chemistryTags: ["Atalanta 2018", "Duvan", "Colombia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 37 presenze, 23 gol.",
  },
  "ilicic-ata-18": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 91,
    passing: 91,
    dribbling: 94,
    physical: 83,
    finishing: 91,
    creativity: 94,
    workRate: 84,
    legacy: 92,
    chemistryTags: ["Atalanta 2018", "Mancino", "Slovenia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 31 presenze, 12 gol.",
  },
  "toloi-ata-18": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 74,
    passing: 80,
    dribbling: 78,
    defending: 86,
    physical: 87,
    creativity: 80,
    workRate: 87,
    legacy: 86,
    chemistryTags: ["Atalanta 2018", "Braccetto", "Brasile"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Atalanta 2018-19: 21 presenze, 1 gol.",
  },
  "zenga-inter-88": {
    dataLevel: "curated-high",
    defending: 95,
    physical: 88,
    passing: 70,
    workRate: 94,
    legacy: 95,
    chemistryTags: ["Inter dei record", "Uomo Ragno", "Portiere"],
    sourceNote: "Profilo curato: Inter 1988-89.",
  },
  "garella-nap-86": {
    dataLevel: "curated-medium",
    defending: 89,
    physical: 84,
    passing: 68,
    workRate: 90,
    legacy: 90,
    chemistryTags: ["Napoli scudetto", "Portiere", "Anni 80"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 29 presenze, tutte da titolare.",
  },
  "ferrara-nap-86": {
    dataLevel: "curated-high",
    pace: 82,
    passing: 78,
    defending: 93,
    physical: 90,
    workRate: 92,
    legacy: 94,
    chemistryTags: ["Napoli scudetto", "Ciro giovane", "Linea difensiva"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 28 presenze, 2 gol.",
  },
  "bruscolotti-nap-86": {
    dataLevel: "curated-high",
    pace: 76,
    passing: 74,
    defending: 92,
    physical: 92,
    workRate: 94,
    legacy: 95,
    chemistryTags: ["Napoli scudetto", "Capitano", "Pal e fierro"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 25 presenze, 21 da titolare.",
  },
  "ferrario-nap-86": {
    dataLevel: "curated-medium",
    pace: 75,
    passing: 72,
    defending: 90,
    physical: 88,
    workRate: 91,
    legacy: 89,
    chemistryTags: ["Napoli scudetto", "Linea difensiva", "Titolare fisso"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 29 presenze, tutte da titolare.",
  },
  "renica-nap-86": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 73,
    defending: 89,
    physical: 89,
    workRate: 91,
    legacy: 89,
    chemistryTags: ["Napoli scudetto", "Linea difensiva", "Ennerre"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 29 presenze, tutte da titolare.",
  },
  "volpecina-nap-86": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 75,
    passing: 80,
    dribbling: 80,
    defending: 83,
    physical: 82,
    creativity: 80,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Napoli scudetto", "Fascia sinistra", "Jolly"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 25 presenze, 2 gol.",
  },
  "bagni-nap-86": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 83,
    passing: 84,
    dribbling: 82,
    defending: 88,
    physical: 91,
    finishing: 82,
    creativity: 84,
    workRate: 96,
    legacy: 91,
    chemistryTags: ["Napoli scudetto", "Pressing", "Inserimento"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 28 presenze, 4 gol.",
  },
  "denapoli-nap-86": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 78,
    passing: 84,
    dribbling: 82,
    defending: 85,
    physical: 88,
    creativity: 82,
    workRate: 94,
    legacy: 88,
    chemistryTags: ["Napoli scudetto", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 28 presenze, 2 gol.",
  },
  "romano-nap-86": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 74,
    passing: 84,
    dribbling: 79,
    defending: 80,
    physical: 82,
    creativity: 82,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Napoli scudetto", "Regia", "Equilibrio"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 22 presenze, tutte da titolare.",
  },
  "giordano-nap-86": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 90,
    passing: 86,
    dribbling: 89,
    finishing: 90,
    creativity: 90,
    workRate: 84,
    legacy: 91,
    chemistryTags: ["Napoli scudetto", "Spalla di Diego", "MA-GI"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 26 presenze, 5 gol.",
  },
  "carnevale-nap-86": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 87,
    passing: 78,
    dribbling: 82,
    physical: 86,
    finishing: 88,
    workRate: 84,
    legacy: 87,
    chemistryTags: ["Napoli scudetto", "Gol pesanti", "Area"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 27 presenze, 8 gol.",
  },
  "caffarelli-nap-86": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 82,
    passing: 80,
    dribbling: 84,
    finishing: 82,
    creativity: 83,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Napoli scudetto", "Rotazione", "Cult"],
    sourceNote: "Profilo curato con riga StatsCrew Napoli 1986-87: 21 presenze, 3 gol.",
  },
  "antonioli-roma-00": {
    dataLevel: "curated-medium",
    defending: 88,
    physical: 84,
    passing: 70,
    workRate: 89,
    legacy: 89,
    chemistryTags: ["Roma scudetto", "Portiere", "Anni 2000"],
    sourceNote: "Profilo curato con riga worldfootball.net Roma 2000-01: 26 presenze, tutte da titolare.",
  },
  "audero-sam-20": {
    dataLevel: "curated-medium",
    defending: 84,
    physical: 82,
    passing: 70,
    workRate: 86,
    legacy: 84,
    chemistryTags: ["Samp moderna", "Portiere", "Profondita"],
    sourceNote: "Profilo curato: Sampdoria 2020-21.",
  },
  "falcao-roma-82": {
    dataLevel: "curated-high",
    shooting: 88,
    passing: 97,
    dribbling: 91,
    defending: 80,
    creativity: 97,
    workRate: 92,
    legacy: 97,
    chemistryTags: ["Roma scudetto", "Ottavo re", "Regia"],
    sourceNote: "Profilo curato: Roma 1982-83.",
  },
  "panucci-roma-06": {
    dataLevel: "curated-medium",
    pace: 78,
    defending: 90,
    passing: 82,
    physical: 87,
    workRate: 90,
    legacy: 89,
    chemistryTags: ["Roma", "Terzino", "Esperienza"],
    sourceNote: "Profilo curato: Roma 2006-07.",
  },
  "dzeko-roma-16": {
    dataLevel: "curated-high",
    shooting: 95,
    passing: 84,
    dribbling: 86,
    physical: 90,
    finishing: 97,
    workRate: 87,
    legacy: 94,
    chemistryTags: ["Capocannoniere", "Roma", "Area"],
    sourceNote: "Profilo curato: Roma 2016-17.",
  },
  "asprilla-parma-93": {
    dataLevel: "curated-high",
    pace: 93,
    shooting: 89,
    passing: 84,
    dribbling: 94,
    finishing: 88,
    creativity: 91,
    legacy: 92,
    chemistryTags: ["Parma anni 90", "Tino", "Caos creativo"],
    sourceNote: "Profilo curato: Parma 1993-94.",
  },
  "gilardino-parma-04": {
    dataLevel: "curated-high",
    shooting: 94,
    passing: 80,
    dribbling: 86,
    physical: 87,
    finishing: 96,
    workRate: 88,
    legacy: 93,
    chemistryTags: ["Parma", "Gila", "Bomber"],
    sourceNote: "Profilo curato: Parma 2004-05.",
  },
  "brolin-parma-93": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 87,
    passing: 89,
    dribbling: 90,
    finishing: 87,
    creativity: 90,
    workRate: 88,
    legacy: 90,
    chemistryTags: ["Parma anni 90", "Jolly", "Svezia"],
    sourceNote: "Profilo curato: Parma 1993-94.",
  },
  "mihajlovic-sam-94": {
    dataLevel: "curated-high",
    shooting: 88,
    passing: 90,
    defending: 90,
    physical: 88,
    creativity: 86,
    workRate: 90,
    legacy: 91,
    chemistryTags: ["Sampdoria", "Punizioni", "Mancino"],
    sourceNote: "Profilo curato: Sampdoria 1994-95.",
  },
  "seedorf-sam-95": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 84,
    passing: 90,
    dribbling: 89,
    physical: 86,
    creativity: 90,
    workRate: 88,
    legacy: 90,
    chemistryTags: ["Sampdoria", "Talento europeo", "Mediana"],
    sourceNote: "Profilo curato: Sampdoria 1995-96.",
  },
  "flachi-sam-04": {
    dataLevel: "curated-medium",
    shooting: 89,
    passing: 85,
    dribbling: 90,
    finishing: 90,
    creativity: 89,
    workRate: 86,
    legacy: 90,
    chemistryTags: ["Sampdoria", "Marassi", "Cult"],
    sourceNote: "Profilo curato: Sampdoria 2004-05.",
  },
  "brivio-vic-97": {
    dataLevel: "curated-medium",
    defending: 84,
    physical: 82,
    passing: 68,
    workRate: 87,
    legacy: 83,
    chemistryTags: ["Vicenza 97", "Menti", "Portiere"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 32 presenze, tutte da titolare.",
  },
  "dicara-vic-97": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 62,
    passing: 72,
    defending: 86,
    physical: 86,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Vicenza 97", "Difesa", "Gol"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 29 presenze, 3 gol.",
  },
  "coco-vic-97": {
    dataLevel: "curated-medium",
    pace: 86,
    passing: 78,
    dribbling: 80,
    defending: 83,
    physical: 80,
    creativity: 78,
    workRate: 84,
    legacy: 86,
    chemistryTags: ["Vicenza 97", "Fascia", "Milan"],
    sourceNote: "Profilo curato con riga BDFutbol Vicenza 1997-98: 20 presenze, 17 da titolare.",
  },
  "belotti-vic-97": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 70,
    defending: 84,
    physical: 85,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Vicenza 97", "Linea", "Operaio"],
    sourceNote: "Profilo curato con riga BDFutbol Vicenza 1997-98: 27 presenze, 26 da titolare.",
  },
  "mendez-vic-97": {
    dataLevel: "curated-medium",
    pace: 77,
    shooting: 60,
    passing: 75,
    defending: 85,
    physical: 88,
    workRate: 87,
    legacy: 84,
    chemistryTags: ["Vicenza 97", "Uruguay", "Duelli"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 26 presenze, 1 gol.",
  },
  "dicarlo-vic-97": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 74,
    passing: 84,
    dribbling: 80,
    defending: 84,
    physical: 88,
    creativity: 82,
    workRate: 95,
    legacy: 87,
    chemistryTags: ["Vicenza 97", "Capitano", "Guidolin"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 31 presenze, 2 gol.",
  },
  "viviani-vic-97": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 68,
    passing: 83,
    dribbling: 78,
    defending: 80,
    physical: 84,
    creativity: 82,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Vicenza 97", "Mediana", "Equilibrio"],
    sourceNote: "Profilo curato con riga BDFutbol Vicenza 1997-98: 30 presenze, 29 da titolare.",
  },
  "schenardi-vic-97": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 78,
    passing: 84,
    dribbling: 85,
    defending: 68,
    physical: 76,
    creativity: 86,
    workRate: 86,
    legacy: 85,
    chemistryTags: ["Vicenza 97", "Esterno", "Cross"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 31 presenze, 2 gol.",
  },
  "ambrosini-vic-97": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 78,
    passing: 84,
    dribbling: 81,
    defending: 86,
    physical: 90,
    creativity: 82,
    workRate: 94,
    legacy: 88,
    chemistryTags: ["Vicenza 97", "Milan", "Contrasti"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 27 presenze, 1 gol.",
  },
  "ambrosetti-vic-97": {
    dataLevel: "curated-medium",
    pace: 88,
    shooting: 82,
    passing: 82,
    dribbling: 87,
    defending: 62,
    physical: 78,
    finishing: 82,
    creativity: 86,
    workRate: 84,
    legacy: 86,
    chemistryTags: ["Vicenza 97", "Strappi", "Fascia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 30 presenze, 5 gol.",
  },
  "luiso-vic-97": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 88,
    passing: 74,
    dribbling: 80,
    physical: 89,
    finishing: 89,
    workRate: 84,
    legacy: 88,
    chemistryTags: ["Vicenza 97", "Toro di Sora", "Coppa"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 29 presenze, 8 gol.",
  },
  "zauli-vic-97": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 82,
    passing: 87,
    dribbling: 88,
    physical: 74,
    finishing: 82,
    creativity: 90,
    workRate: 80,
    legacy: 86,
    chemistryTags: ["Vicenza 97", "Dieci nascosto", "Creatore"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 24 presenze, 4 gol.",
  },
  "dinapoli-vic-97": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 86,
    passing: 76,
    dribbling: 84,
    physical: 78,
    finishing: 87,
    creativity: 80,
    workRate: 82,
    legacy: 86,
    chemistryTags: ["Vicenza 97", "Re Artu", "Rotazione"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 24 presenze, 6 gol.",
  },
  "otero-vic-97": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 83,
    passing: 76,
    dribbling: 83,
    physical: 80,
    finishing: 84,
    creativity: 78,
    workRate: 78,
    legacy: 84,
    chemistryTags: ["Vicenza 97", "Uruguay", "Cult"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Vicenza 1997-98: 15 presenze, 2 gol.",
  },
  "stovini-vic-97": {
    dataLevel: "curated-medium",
    pace: 75,
    passing: 68,
    defending: 82,
    physical: 84,
    workRate: 85,
    legacy: 82,
    chemistryTags: ["Vicenza 97", "Difesa", "Profondita"],
    sourceNote: "Profilo curato con riga BDFutbol Vicenza 1997-98: 17 presenze, 12 da titolare.",
  },
  "srnicek-bre-00": {
    dataLevel: "curated-medium",
    defending: 85,
    physical: 84,
    passing: 68,
    workRate: 87,
    legacy: 84,
    chemistryTags: ["Brescia 2000", "Mazzone", "Portiere"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 26 presenze, tutte da titolare.",
  },
  "calori-bre-00": {
    dataLevel: "curated-medium",
    pace: 70,
    shooting: 58,
    passing: 72,
    defending: 86,
    physical: 88,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Brescia 2000", "Esperienza", "Linea"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 31 presenze, 1 gol.",
  },
  "petruzzi-bre-00": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 56,
    passing: 70,
    defending: 85,
    physical: 87,
    workRate: 87,
    legacy: 84,
    chemistryTags: ["Brescia 2000", "Marcatura", "Roma"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 28 presenze, 1 gol.",
  },
  "bonera-bre-00": {
    dataLevel: "curated-medium",
    pace: 78,
    passing: 72,
    defending: 84,
    physical: 84,
    workRate: 86,
    legacy: 85,
    chemistryTags: ["Brescia 2000", "Baby", "Difesa"],
    sourceNote: "Profilo curato con riga BDFutbol Brescia 2000-01: 26 presenze, 25 da titolare.",
  },
  "diana-bre-00": {
    dataLevel: "curated-medium",
    pace: 86,
    shooting: 72,
    passing: 80,
    dribbling: 80,
    defending: 80,
    physical: 82,
    creativity: 79,
    workRate: 92,
    legacy: 86,
    chemistryTags: ["Brescia 2000", "Fascia", "Jolly"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 32 presenze, 2 gol.",
  },
  "bisoli-bre-00": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 70,
    passing: 80,
    dribbling: 76,
    defending: 82,
    physical: 86,
    creativity: 78,
    workRate: 93,
    legacy: 84,
    chemistryTags: ["Brescia 2000", "Mediano", "Lotta"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 32 presenze, 2 gol.",
  },
  "afilippini-bre-00": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 70,
    passing: 79,
    dribbling: 78,
    defending: 78,
    physical: 82,
    creativity: 78,
    workRate: 94,
    legacy: 84,
    chemistryTags: ["Brescia 2000", "Filippini", "Pressing"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 28 presenze, 1 gol.",
  },
  "efilippini-bre-00": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 66,
    passing: 78,
    dribbling: 77,
    defending: 78,
    physical: 82,
    creativity: 76,
    workRate: 93,
    legacy: 83,
    chemistryTags: ["Brescia 2000", "Filippini", "Motore"],
    sourceNote: "Profilo curato con riga BDFutbol Brescia 2000-01: 28 presenze, 23 da titolare.",
  },
  "pirlo-bre-00": {
    dataLevel: "curated-high",
    pace: 70,
    shooting: 78,
    passing: 91,
    dribbling: 84,
    defending: 68,
    physical: 72,
    creativity: 92,
    workRate: 80,
    legacy: 88,
    chemistryTags: ["Brescia 2000", "Pirlo-Baggio", "Regia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 10 presenze, tutte da titolare.",
  },
  "baggio-bre-00": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 92,
    passing: 94,
    dribbling: 95,
    physical: 74,
    finishing: 93,
    creativity: 96,
    workRate: 82,
    legacy: 97,
    chemistryTags: ["Brescia 2000", "Codino", "Mazzone"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 25 presenze, 10 gol.",
  },
  "hubner-bre-00": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 91,
    passing: 72,
    dribbling: 78,
    physical: 89,
    finishing: 93,
    creativity: 76,
    workRate: 84,
    legacy: 91,
    chemistryTags: ["Brescia 2000", "Bisonte", "Provincia"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 31 presenze, 17 gol.",
  },
  "tare-bre-00": {
    dataLevel: "curated-medium",
    pace: 70,
    shooting: 80,
    passing: 70,
    dribbling: 72,
    physical: 88,
    finishing: 82,
    workRate: 80,
    legacy: 83,
    chemistryTags: ["Brescia 2000", "Albania", "Torre"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 13 presenze.",
  },
  "bachini-bre-00": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 76,
    passing: 81,
    dribbling: 84,
    defending: 68,
    physical: 76,
    finishing: 76,
    creativity: 83,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Brescia 2000", "Fascia", "Strappi"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Brescia 2000-01: 20 presenze, 2 gol.",
  },
  "sereni-pia-97": {
    dataLevel: "curated-medium",
    defending: 85,
    physical: 83,
    passing: 66,
    workRate: 90,
    legacy: 84,
    chemistryTags: ["Piacenza 97", "Garilli", "Portiere"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 34 presenze, tutte da titolare.",
  },
  "tramezzani-pia-97": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 60,
    passing: 76,
    dribbling: 76,
    defending: 84,
    physical: 82,
    workRate: 92,
    legacy: 85,
    chemistryTags: ["Piacenza 97", "Fascia", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 32 presenze, 1 gol.",
  },
  "delli-carri-pia-97": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 55,
    passing: 70,
    defending: 85,
    physical: 88,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Piacenza 97", "Stopper", "Duelli"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 31 presenze, 1 gol.",
  },
  "vierchowod-pia-97": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 64,
    passing: 74,
    defending: 90,
    physical: 92,
    workRate: 90,
    legacy: 89,
    chemistryTags: ["Piacenza 97", "Zar", "Esperienza"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 29 presenze, 2 gol.",
  },
  "mazzola-pia-97": {
    dataLevel: "curated-medium",
    pace: 77,
    shooting: 58,
    passing: 76,
    defending: 82,
    physical: 84,
    creativity: 74,
    workRate: 91,
    legacy: 83,
    chemistryTags: ["Piacenza 97", "Jolly", "Sobrio"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 30 presenze, tutte da titolare.",
  },
  "marcorossi-pia-97": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 68,
    defending: 83,
    physical: 84,
    workRate: 89,
    legacy: 83,
    chemistryTags: ["Piacenza 97", "Marcatura", "Profondita"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 24 presenze, tutte da titolare.",
  },
  "scienza-pia-97": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 78,
    passing: 84,
    dribbling: 80,
    defending: 75,
    physical: 80,
    creativity: 84,
    workRate: 90,
    legacy: 86,
    chemistryTags: ["Piacenza 97", "Regia operaia", "Gol"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 31 presenze, 3 gol.",
  },
  "stroppa-pia-97": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 75,
    passing: 86,
    dribbling: 84,
    defending: 70,
    physical: 76,
    creativity: 87,
    workRate: 82,
    legacy: 86,
    chemistryTags: ["Piacenza 97", "Piede educato", "Tecnica"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 20 presenze, 18 da titolare.",
  },
  "valoti-pia-97": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 66,
    passing: 78,
    dribbling: 76,
    defending: 78,
    physical: 82,
    creativity: 76,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Piacenza 97", "Mediana", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 21 presenze.",
  },
  "bordin-pia-97": {
    dataLevel: "curated-medium",
    pace: 73,
    shooting: 65,
    passing: 79,
    dribbling: 75,
    defending: 80,
    physical: 83,
    creativity: 76,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Piacenza 97", "Cerniera", "Copertura"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 21 presenze.",
  },
  "piovani-pia-97": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 84,
    passing: 80,
    dribbling: 82,
    physical: 80,
    finishing: 85,
    creativity: 82,
    workRate: 88,
    legacy: 86,
    chemistryTags: ["Piacenza 97", "Raccordo", "Cinque gol"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 30 presenze, 5 gol.",
  },
  "dionigi-pia-97": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 84,
    passing: 72,
    dribbling: 78,
    physical: 84,
    finishing: 85,
    creativity: 74,
    workRate: 86,
    legacy: 85,
    chemistryTags: ["Piacenza 97", "Nove", "Concreto"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 23 presenze, 5 gol.",
  },
  "rastelli-pia-97": {
    dataLevel: "curated-medium",
    pace: 81,
    shooting: 78,
    passing: 76,
    dribbling: 80,
    physical: 78,
    finishing: 79,
    creativity: 78,
    workRate: 86,
    legacy: 84,
    chemistryTags: ["Piacenza 97", "Cambio", "Pressing"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 31 presenze, 2 gol.",
  },
  "murgita-pia-97": {
    dataLevel: "curated-medium",
    pace: 77,
    shooting: 82,
    passing: 72,
    dribbling: 77,
    physical: 82,
    finishing: 84,
    creativity: 73,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Piacenza 97", "Rotazione", "Cinque gol"],
    sourceNote: "Profilo curato con riga BDFutbol Piacenza 1997-98: 27 presenze, 5 gol.",
  },
  "taibi-ven-98": {
    dataLevel: "curated-medium",
    pace: 70,
    passing: 68,
    defending: 86,
    physical: 84,
    workRate: 90,
    legacy: 86,
    chemistryTags: ["Venezia 98", "Penzo", "Portiere"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 33 presenze, tutte da titolare.",
  },
  "dalcanto-ven-98": {
    dataLevel: "curated-medium",
    pace: 73,
    passing: 70,
    defending: 84,
    physical: 84,
    workRate: 90,
    legacy: 84,
    chemistryTags: ["Venezia 98", "Linea", "Marcatura"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 27 presenze, 26 da titolare.",
  },
  "pavan-ven-98": {
    dataLevel: "curated-medium",
    pace: 74,
    passing: 70,
    defending: 84,
    physical: 85,
    workRate: 89,
    legacy: 84,
    chemistryTags: ["Venezia 98", "Duelli", "Salvezza"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 26 presenze, 25 da titolare.",
  },
  "luppi-ven-98": {
    dataLevel: "curated-medium",
    pace: 72,
    passing: 72,
    defending: 86,
    physical: 86,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Venezia 98", "Esperienza", "Cartellini"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 31 presenze, 30 da titolare.",
  },
  "brioschi-ven-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 58,
    passing: 75,
    dribbling: 74,
    defending: 80,
    physical: 80,
    workRate: 90,
    legacy: 83,
    chemistryTags: ["Venezia 98", "Jolly", "Fascia"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 24 presenze, 23 da titolare.",
  },
  "pedone-ven-98": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 76,
    passing: 80,
    dribbling: 76,
    defending: 79,
    physical: 83,
    finishing: 76,
    creativity: 79,
    workRate: 91,
    legacy: 85,
    chemistryTags: ["Venezia 98", "Mediana", "Tre gol"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 33 presenze, 3 gol.",
  },
  "miceli-ven-98": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 68,
    passing: 78,
    dribbling: 74,
    defending: 80,
    physical: 82,
    creativity: 76,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Venezia 98", "Polmoni", "Ossatura"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 27 presenze, 1 gol.",
  },
  "volpi-ven-98": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 72,
    passing: 86,
    dribbling: 80,
    defending: 76,
    physical: 78,
    creativity: 87,
    workRate: 86,
    legacy: 87,
    chemistryTags: ["Venezia 98", "Regia", "Giovane"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 27 presenze, 1 gol.",
  },
  "valtolina-ven-98": {
    dataLevel: "curated-medium",
    pace: 84,
    shooting: 76,
    passing: 78,
    dribbling: 82,
    defending: 70,
    physical: 78,
    finishing: 78,
    creativity: 80,
    workRate: 90,
    legacy: 84,
    chemistryTags: ["Venezia 98", "Fascia", "Cult"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 29 presenze, 3 gol.",
  },
  "maniero-ven-98": {
    dataLevel: "curated-high",
    pace: 77,
    shooting: 88,
    passing: 72,
    dribbling: 78,
    physical: 86,
    finishing: 90,
    creativity: 74,
    workRate: 84,
    legacy: 89,
    chemistryTags: ["Venezia 98", "Maniero-Recoba", "Area"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 27 presenze, 12 gol.",
  },
  "recoba-ven-98": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 93,
    passing: 91,
    dribbling: 94,
    physical: 76,
    finishing: 91,
    creativity: 95,
    workRate: 80,
    legacy: 94,
    chemistryTags: ["Venezia 98", "Chino", "Salvezza"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 19 presenze, 10 gol.",
  },
  "carnasciali-ven-98": {
    dataLevel: "curated-medium",
    pace: 72,
    passing: 70,
    defending: 82,
    physical: 83,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Venezia 98", "Esperienza", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 16 presenze, tutte da titolare.",
  },
  "iachini-ven-98": {
    dataLevel: "curated-medium",
    pace: 70,
    shooting: 62,
    passing: 76,
    dribbling: 72,
    defending: 81,
    physical: 86,
    creativity: 72,
    workRate: 90,
    legacy: 83,
    chemistryTags: ["Venezia 98", "Mediano", "Ruvido"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 15 presenze, tutte da titolare.",
  },
  "schwoch-ven-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 82,
    passing: 70,
    dribbling: 78,
    physical: 80,
    finishing: 84,
    creativity: 72,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Venezia 98", "Punta cult", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Venezia 1998-99: 14 presenze, 2 gol.",
  },
  "taibi-reg-99": {
    dataLevel: "curated-medium",
    pace: 70,
    passing: 66,
    defending: 85,
    physical: 85,
    workRate: 90,
    legacy: 85,
    chemistryTags: ["Reggina 99", "Granillo", "Portiere"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Reggina 1999-00: 18 presenze.",
  },
  "orlandoni-reg-99": {
    dataLevel: "curated-medium",
    pace: 68,
    passing: 64,
    defending: 82,
    physical: 82,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Reggina 99", "Porta", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 13 presenze, tutte da titolare.",
  },
  "stovini-reg-99": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 70,
    defending: 86,
    physical: 86,
    workRate: 93,
    legacy: 85,
    chemistryTags: ["Reggina 99", "Muro", "Sempre titolare"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 34 presenze, tutte da titolare.",
  },
  "cirillo-reg-99": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 64,
    passing: 70,
    defending: 85,
    physical: 88,
    workRate: 91,
    legacy: 85,
    chemistryTags: ["Reggina 99", "Marcatura", "Due gol"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 32 presenze, 2 gol.",
  },
  "giacchetta-reg-99": {
    dataLevel: "curated-medium",
    pace: 79,
    shooting: 66,
    passing: 76,
    dribbling: 74,
    defending: 80,
    physical: 82,
    creativity: 74,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Reggina 99", "Fascia", "Caposaldo"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 30 presenze, 1 gol.",
  },
  "morabito-reg-99": {
    dataLevel: "curated-medium",
    pace: 81,
    shooting: 60,
    passing: 75,
    dribbling: 75,
    defending: 79,
    physical: 82,
    creativity: 74,
    workRate: 92,
    legacy: 84,
    chemistryTags: ["Reggina 99", "Fascia", "Corsa"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 30 presenze, tutte da titolare.",
  },
  "baronio-reg-99": {
    dataLevel: "curated-high",
    pace: 74,
    shooting: 78,
    passing: 87,
    dribbling: 80,
    defending: 72,
    physical: 78,
    finishing: 76,
    creativity: 88,
    workRate: 86,
    legacy: 87,
    chemistryTags: ["Reggina 99", "Regia", "Under"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 31 presenze, 3 gol.",
  },
  "brevi-reg-99": {
    dataLevel: "curated-medium",
    pace: 73,
    shooting: 62,
    passing: 77,
    dribbling: 73,
    defending: 82,
    physical: 86,
    creativity: 74,
    workRate: 92,
    legacy: 84,
    chemistryTags: ["Reggina 99", "Mediana", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 28 presenze.",
  },
  "pirlo-reg-99": {
    dataLevel: "curated-high",
    pace: 72,
    shooting: 83,
    passing: 91,
    dribbling: 84,
    defending: 64,
    physical: 72,
    finishing: 82,
    creativity: 93,
    workRate: 80,
    legacy: 90,
    chemistryTags: ["Reggina 99", "Pirlo giovane", "Trequarti"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 28 presenze, 6 gol.",
  },
  "pralija-reg-99": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 66,
    passing: 78,
    dribbling: 76,
    defending: 74,
    physical: 80,
    creativity: 78,
    workRate: 86,
    legacy: 83,
    chemistryTags: ["Reggina 99", "Balcanica", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 23 presenze.",
  },
  "kallon-reg-99": {
    dataLevel: "curated-high",
    pace: 90,
    shooting: 87,
    passing: 74,
    dribbling: 84,
    physical: 80,
    finishing: 88,
    creativity: 78,
    workRate: 84,
    legacy: 88,
    chemistryTags: ["Reggina 99", "Granillo", "Undici gol"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Reggina 1999-00: 30 presenze, 11 gol.",
  },
  "possanzini-reg-99": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 78,
    passing: 76,
    dribbling: 80,
    physical: 78,
    finishing: 79,
    creativity: 78,
    workRate: 88,
    legacy: 84,
    chemistryTags: ["Reggina 99", "Seconda punta", "Incastro"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 31 presenze, 3 gol.",
  },
  "cozza-reg-99": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 78,
    passing: 82,
    dribbling: 83,
    physical: 74,
    finishing: 78,
    creativity: 86,
    workRate: 82,
    legacy: 85,
    chemistryTags: ["Reggina 99", "Dieci", "Amaranto"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 17 presenze, 2 gol.",
  },
  "bogdani-reg-99": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 80,
    passing: 68,
    dribbling: 74,
    physical: 86,
    finishing: 82,
    creativity: 70,
    workRate: 84,
    legacy: 83,
    chemistryTags: ["Reggina 99", "Torre", "Albania"],
    sourceNote: "Profilo curato con riga BDFutbol Reggina 1999-00: 10 presenze, 2 gol.",
  },
  "chimenti-lec-99": {
    dataLevel: "curated-medium",
    pace: 70,
    passing: 66,
    defending: 85,
    physical: 84,
    workRate: 89,
    legacy: 85,
    chemistryTags: ["Lecce 99", "Via del Mare", "Portiere"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Lecce 1999-00: 33 presenze.",
  },
  "balleri-lec-99": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 58,
    passing: 76,
    dribbling: 75,
    defending: 80,
    physical: 82,
    workRate: 92,
    legacy: 84,
    chemistryTags: ["Lecce 99", "Fascia", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 32 presenze, tutte da titolare.",
  },
  "savino-lec-99": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 62,
    passing: 70,
    defending: 84,
    physical: 85,
    workRate: 90,
    legacy: 84,
    chemistryTags: ["Lecce 99", "Linea", "Salvezza"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 31 presenze, 1 gol.",
  },
  "juarez-lec-99": {
    dataLevel: "curated-medium",
    pace: 76,
    passing: 70,
    defending: 86,
    physical: 88,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Lecce 99", "Brasile", "Centrale"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 33 presenze.",
  },
  "viali-lec-99": {
    dataLevel: "curated-medium",
    pace: 73,
    shooting: 62,
    passing: 70,
    defending: 84,
    physical: 86,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Lecce 99", "Titolare", "Difesa"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 32 presenze, 1 gol.",
  },
  "pivotto-lec-99": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 62,
    passing: 74,
    dribbling: 72,
    defending: 80,
    physical: 82,
    creativity: 72,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Lecce 99", "Jolly", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 27 presenze, 1 gol.",
  },
  "conticchio-lec-99": {
    dataLevel: "curated-medium",
    pace: 77,
    shooting: 76,
    passing: 81,
    dribbling: 78,
    defending: 78,
    physical: 82,
    finishing: 76,
    creativity: 80,
    workRate: 92,
    legacy: 85,
    chemistryTags: ["Lecce 99", "Mezzala", "Quattro gol"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 33 presenze, 4 gol.",
  },
  "lima-lec-99": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 72,
    passing: 84,
    dribbling: 82,
    defending: 78,
    physical: 83,
    creativity: 84,
    workRate: 87,
    legacy: 86,
    chemistryTags: ["Lecce 99", "Brasiliano", "Centrocampo"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 32 presenze, 1 gol.",
  },
  "piangerelli-lec-99": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 62,
    passing: 78,
    dribbling: 74,
    defending: 80,
    physical: 82,
    creativity: 76,
    workRate: 90,
    legacy: 84,
    chemistryTags: ["Lecce 99", "Cerniera", "Equilibrio"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 29 presenze.",
  },
  "lucarelli-lec-99": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 89,
    passing: 72,
    dribbling: 78,
    physical: 88,
    finishing: 91,
    creativity: 74,
    workRate: 86,
    legacy: 90,
    chemistryTags: ["Lecce 99", "Quindici gol", "Via del Mare"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Lecce 1999-00: 30 presenze, 15 gol.",
  },
  "sesa-lec-99": {
    dataLevel: "curated-high",
    pace: 82,
    shooting: 84,
    passing: 85,
    dribbling: 86,
    physical: 76,
    finishing: 84,
    creativity: 88,
    workRate: 86,
    legacy: 86,
    chemistryTags: ["Lecce 99", "Svizzerino", "Assist"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Lecce 1999-00: 29 presenze, 7 gol, 5 assist.",
  },
  "colonnello-lec-99": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 58,
    passing: 74,
    dribbling: 74,
    defending: 79,
    physical: 80,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Lecce 99", "Fascia", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 27 presenze.",
  },
  "bonomi-lec-99": {
    dataLevel: "curated-medium",
    pace: 79,
    shooting: 78,
    passing: 74,
    dribbling: 78,
    physical: 78,
    finishing: 80,
    creativity: 76,
    workRate: 84,
    legacy: 83,
    chemistryTags: ["Lecce 99", "Cambio", "Tre gol"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 24 presenze, 3 gol.",
  },
  "biliotti-lec-99": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 70,
    passing: 75,
    dribbling: 77,
    physical: 76,
    creativity: 76,
    workRate: 84,
    legacy: 82,
    chemistryTags: ["Lecce 99", "Jolly", "Panchina"],
    sourceNote: "Profilo curato con riga BDFutbol Lecce 1999-00: 19 presenze.",
  },
  "balli-sal-98": {
    dataLevel: "curated-medium",
    pace: 66,
    passing: 64,
    defending: 84,
    physical: 82,
    workRate: 88,
    legacy: 84,
    chemistryTags: ["Salernitana 98", "Arechi", "Portiere"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Salernitana 1998-99: 31 presenze.",
  },
  "delgrosso-sal-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 58,
    passing: 74,
    dribbling: 73,
    defending: 81,
    physical: 81,
    workRate: 91,
    legacy: 84,
    chemistryTags: ["Salernitana 98", "Fascia", "Trentuno partenze"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 31 presenze, 1 gol.",
  },
  "tosto-sal-98": {
    dataLevel: "curated-medium",
    pace: 79,
    shooting: 57,
    passing: 74,
    dribbling: 75,
    defending: 79,
    physical: 80,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Salernitana 98", "Fascia", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 27 presenze.",
  },
  "fresi-sal-98": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 70,
    passing: 78,
    defending: 86,
    physical: 87,
    creativity: 76,
    workRate: 88,
    legacy: 87,
    chemistryTags: ["Salernitana 98", "Fresi", "Tre gol"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 27 presenze, 3 gol.",
  },
  "fusco-sal-98": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 50,
    passing: 68,
    defending: 82,
    physical: 84,
    workRate: 90,
    legacy: 83,
    chemistryTags: ["Salernitana 98", "Linea", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 24 presenze.",
  },
  "bolic-sal-98": {
    dataLevel: "curated-medium",
    pace: 73,
    shooting: 60,
    passing: 68,
    defending: 82,
    physical: 86,
    workRate: 87,
    legacy: 84,
    chemistryTags: ["Salernitana 98", "Croazia", "Centrale"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 31 presenze, 1 gol.",
  },
  "breda-sal-98": {
    dataLevel: "curated-medium",
    pace: 73,
    shooting: 70,
    passing: 82,
    dribbling: 76,
    defending: 80,
    physical: 83,
    creativity: 79,
    workRate: 92,
    legacy: 85,
    chemistryTags: ["Salernitana 98", "Capitano", "Mediana"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 26 presenze, 2 gol.",
  },
  "gattuso-sal-98": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 60,
    passing: 78,
    dribbling: 74,
    defending: 86,
    physical: 90,
    workRate: 97,
    legacy: 92,
    chemistryTags: ["Salernitana 98", "Ringhio", "Scozia-Milan"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Salernitana 1998-99: 25 presenze prima del Milan.",
  },
  "vannucchi-sal-98": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 78,
    passing: 84,
    dribbling: 87,
    physical: 74,
    finishing: 78,
    creativity: 88,
    workRate: 84,
    legacy: 86,
    chemistryTags: ["Salernitana 98", "Trequarti", "Fantasia"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 31 presenze, 3 gol.",
  },
  "divaio-sal-98": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 88,
    passing: 78,
    dribbling: 84,
    physical: 80,
    finishing: 90,
    creativity: 82,
    workRate: 86,
    legacy: 90,
    chemistryTags: ["Salernitana 98", "Di Vaio", "Dodici gol"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Salernitana 1998-99: 31 presenze, 12 gol, 6 assist.",
  },
  "giampaolo-sal-98": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 80,
    passing: 78,
    dribbling: 80,
    physical: 77,
    finishing: 81,
    creativity: 80,
    workRate: 84,
    legacy: 84,
    chemistryTags: ["Salernitana 98", "Seconda punta", "Assist"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Salernitana 1998-99: 24 presenze, 3 gol, 4 assist.",
  },
  "bernardini-sal-98": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 65,
    passing: 77,
    dribbling: 75,
    defending: 78,
    physical: 80,
    creativity: 75,
    workRate: 89,
    legacy: 83,
    chemistryTags: ["Salernitana 98", "Mediana", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Salernitana 1998-99: 20 presenze, 1 gol.",
  },
  "dimichele-sal-98": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 82,
    passing: 76,
    dribbling: 85,
    physical: 76,
    finishing: 82,
    creativity: 81,
    workRate: 84,
    legacy: 86,
    chemistryTags: ["Salernitana 98", "Di Michele", "Giovane"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Salernitana 1998-99: 26 presenze, 3 gol.",
  },
  "song-sal-98": {
    dataLevel: "curated-high",
    pace: 81,
    shooting: 62,
    passing: 70,
    defending: 84,
    physical: 88,
    workRate: 88,
    legacy: 88,
    chemistryTags: ["Salernitana 98", "Song", "Liverpool a ottobre"],
    sourceNote: "Profilo curato con righe BDFutbol/StatsCrew Salernitana 1998-99: 4 presenze, 1 gol.",
  },
  "roccati-emp-97": {
    dataLevel: "curated-medium",
    pace: 66,
    passing: 62,
    defending: 83,
    physical: 82,
    workRate: 87,
    legacy: 82,
    chemistryTags: ["Empoli 97", "Castellani", "Portiere"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 28 presenze, 27 partenze.",
  },
  "pfusco-emp-97": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 58,
    passing: 74,
    dribbling: 73,
    defending: 80,
    physical: 82,
    creativity: 73,
    workRate: 91,
    legacy: 82,
    chemistryTags: ["Empoli 97", "Grinder", "Spalletti"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 31 presenze, 31 partenze.",
  },
  "pane-emp-97": {
    dataLevel: "curated-medium",
    pace: 72,
    shooting: 64,
    passing: 70,
    defending: 82,
    physical: 83,
    workRate: 90,
    legacy: 83,
    chemistryTags: ["Empoli 97", "Linea", "Castellani"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 33 presenze, 32 partenze, 2 gol.",
  },
  "bianconi-emp-97": {
    dataLevel: "curated-medium",
    pace: 70,
    shooting: 48,
    passing: 66,
    defending: 84,
    physical: 86,
    workRate: 90,
    legacy: 83,
    chemistryTags: ["Empoli 97", "Centrale", "Grinder"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 32 presenze, 32 partenze.",
  },
  "baldini-emp-97": {
    dataLevel: "curated-medium",
    pace: 68,
    shooting: 57,
    passing: 68,
    defending: 83,
    physical: 87,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Empoli 97", "Esperienza", "Ruvido"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 30 presenze, 29 partenze, 1 gol.",
  },
  "ficini-emp-97": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 60,
    passing: 76,
    dribbling: 75,
    defending: 78,
    physical: 80,
    creativity: 76,
    workRate: 92,
    legacy: 83,
    chemistryTags: ["Empoli 97", "Nato a Empoli", "Ossatura"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 32 presenze, 31 partenze.",
  },
  "ametrano-emp-97": {
    dataLevel: "curated-medium",
    pace: 82,
    shooting: 70,
    passing: 76,
    dribbling: 82,
    defending: 72,
    physical: 76,
    finishing: 72,
    creativity: 79,
    workRate: 86,
    legacy: 84,
    chemistryTags: ["Empoli 97", "Scuola Juve", "Strappi"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 29 presenze, 27 partenze, 3 gol.",
  },
  "tonetto-emp-97": {
    dataLevel: "curated-medium",
    pace: 83,
    shooting: 70,
    passing: 78,
    dribbling: 80,
    defending: 77,
    physical: 80,
    finishing: 72,
    creativity: 80,
    workRate: 88,
    legacy: 85,
    chemistryTags: ["Empoli 97", "Fascia", "Tre gol"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 30 presenze, 26 partenze, 3 gol.",
  },
  "martusciello-emp-97": {
    dataLevel: "curated-high",
    pace: 76,
    shooting: 78,
    passing: 84,
    dribbling: 85,
    defending: 70,
    physical: 75,
    finishing: 80,
    creativity: 87,
    workRate: 83,
    legacy: 86,
    chemistryTags: ["Empoli 97", "Dieci", "Spalletti"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 23 presenze, 20 partenze, 6 gol.",
  },
  "esposito-emp-97": {
    dataLevel: "curated-high",
    pace: 81,
    shooting: 87,
    passing: 74,
    dribbling: 82,
    physical: 80,
    finishing: 89,
    creativity: 78,
    workRate: 84,
    legacy: 88,
    chemistryTags: ["Empoli 97", "Quattordici gol", "Castellani"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 32 presenze, 31 partenze, 14 gol.",
  },
  "cappellini-emp-97": {
    dataLevel: "curated-high",
    pace: 78,
    shooting: 83,
    passing: 82,
    dribbling: 85,
    physical: 76,
    finishing: 84,
    creativity: 86,
    workRate: 82,
    legacy: 86,
    chemistryTags: ["Empoli 97", "Seconda punta", "Nove gol"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 26 presenze, 22 partenze, 9 gol.",
  },
  "florijancic-emp-97": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 80,
    passing: 72,
    dribbling: 78,
    physical: 78,
    finishing: 81,
    creativity: 73,
    workRate: 82,
    legacy: 83,
    chemistryTags: ["Empoli 97", "Slovenia", "Rotazione"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 28 presenze, 11 partenze, 6 gol.",
  },
  "bonomi-emp-97": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 70,
    passing: 75,
    dribbling: 75,
    defending: 74,
    physical: 78,
    finishing: 72,
    creativity: 75,
    workRate: 87,
    legacy: 82,
    chemistryTags: ["Empoli 97", "Rotazione", "Grinder"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 19 presenze, 18 partenze, 3 gol.",
  },
  "bettella-emp-97": {
    dataLevel: "curated-medium",
    pace: 68,
    shooting: 55,
    passing: 66,
    defending: 78,
    physical: 82,
    workRate: 86,
    legacy: 81,
    chemistryTags: ["Empoli 97", "Panchina", "Difesa"],
    sourceNote: "Profilo curato con righe BDFutbol/FootballSquads Empoli 1997-98: 21 presenze, 7 partenze, 1 gol.",
  },
  "mancini-fog-91": {
    dataLevel: "curated-medium",
    pace: 67,
    passing: 63,
    defending: 84,
    physical: 82,
    workRate: 88,
    legacy: 84,
    chemistryTags: ["Foggia 91", "Zaccheria", "Portiere"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 30 presenze, 30 partenze.",
  },
  "codispoti-fog-91": {
    dataLevel: "curated-medium",
    pace: 75,
    shooting: 61,
    passing: 72,
    defending: 82,
    physical: 83,
    workRate: 90,
    legacy: 84,
    chemistryTags: ["Foggia 91", "Linea alta", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 34 presenze, 33 partenze, 2 gol.",
  },
  "consagra-fog-91": {
    dataLevel: "curated-medium",
    pace: 73,
    shooting: 50,
    passing: 68,
    defending: 81,
    physical: 84,
    workRate: 88,
    legacy: 82,
    chemistryTags: ["Foggia 91", "Copertura", "Zeman"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 29 presenze, 28 partenze.",
  },
  "matrecano-fog-91": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 50,
    passing: 69,
    defending: 82,
    physical: 82,
    workRate: 89,
    legacy: 84,
    chemistryTags: ["Foggia 91", "Ventuno anni", "Linea"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 28 presenze, 27 partenze.",
  },
  "grandini-fog-91": {
    dataLevel: "curated-medium",
    pace: 70,
    shooting: 48,
    passing: 66,
    defending: 79,
    physical: 82,
    workRate: 87,
    legacy: 81,
    chemistryTags: ["Foggia 91", "Rotazione", "Difesa"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 17 presenze, 16 partenze.",
  },
  "barone-fog-91": {
    dataLevel: "curated-medium",
    pace: 76,
    shooting: 58,
    passing: 77,
    dribbling: 75,
    defending: 82,
    physical: 84,
    creativity: 75,
    workRate: 94,
    legacy: 85,
    chemistryTags: ["Foggia 91", "Trentaquattro su 34", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 34 presenze, 34 partenze.",
  },
  "shalimov-fog-91": {
    dataLevel: "curated-high",
    pace: 80,
    shooting: 82,
    passing: 87,
    dribbling: 88,
    defending: 72,
    physical: 78,
    finishing: 82,
    creativity: 90,
    workRate: 85,
    legacy: 90,
    chemistryTags: ["Foggia 91", "Russia", "Nove gol"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 33 presenze, 33 partenze, 9 gol.",
  },
  "petrescu-fog-91": {
    dataLevel: "curated-high",
    pace: 84,
    shooting: 72,
    passing: 80,
    dribbling: 82,
    defending: 80,
    physical: 81,
    finishing: 73,
    creativity: 82,
    workRate: 89,
    legacy: 87,
    chemistryTags: ["Foggia 91", "Romania", "Fascia"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 25 presenze, 24 partenze, 4 gol.",
  },
  "baiano-fog-91": {
    dataLevel: "curated-high",
    pace: 86,
    shooting: 88,
    passing: 78,
    dribbling: 87,
    physical: 76,
    finishing: 90,
    creativity: 82,
    workRate: 86,
    legacy: 90,
    chemistryTags: ["Foggia 91", "Sedici gol", "Zemanlandia"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 33 presenze, 33 partenze, 16 gol.",
  },
  "rambaudi-fog-91": {
    dataLevel: "curated-high",
    pace: 87,
    shooting: 83,
    passing: 82,
    dribbling: 86,
    physical: 78,
    finishing: 84,
    creativity: 84,
    workRate: 88,
    legacy: 88,
    chemistryTags: ["Foggia 91", "Tridente", "Nove gol"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 33 presenze, 32 partenze, 9 gol.",
  },
  "signori-fog-91": {
    dataLevel: "curated-high",
    pace: 85,
    shooting: 88,
    passing: 80,
    dribbling: 86,
    physical: 76,
    finishing: 89,
    creativity: 83,
    workRate: 86,
    legacy: 91,
    chemistryTags: ["Foggia 91", "Beppe", "Prima Lazio"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 32 presenze, 31 partenze, 11 gol.",
  },
  "picasso-fog-91": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 62,
    passing: 76,
    dribbling: 75,
    defending: 76,
    physical: 80,
    creativity: 76,
    workRate: 89,
    legacy: 83,
    chemistryTags: ["Foggia 91", "Picasso", "Grinder"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 20 presenze, 19 partenze, 1 gol.",
  },
  "porro-fog-91": {
    dataLevel: "curated-medium",
    pace: 78,
    shooting: 66,
    passing: 73,
    dribbling: 76,
    defending: 70,
    physical: 76,
    creativity: 74,
    workRate: 85,
    legacy: 82,
    chemistryTags: ["Foggia 91", "Rotazione", "Jolly"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 21 presenze, 8 partenze, 1 gol.",
  },
  "padalino-fog-91": {
    dataLevel: "curated-medium",
    pace: 74,
    shooting: 58,
    passing: 69,
    defending: 80,
    physical: 82,
    workRate: 88,
    legacy: 83,
    chemistryTags: ["Foggia 91", "Padalino", "Giovane"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 15 presenze, 12 partenze, 1 gol.",
  },
  "kolyvanov-fog-91": {
    dataLevel: "curated-medium",
    pace: 80,
    shooting: 80,
    passing: 75,
    dribbling: 82,
    physical: 77,
    finishing: 82,
    creativity: 78,
    workRate: 82,
    legacy: 84,
    chemistryTags: ["Foggia 91", "Russia", "Rotazione"],
    sourceNote: "Profilo curato con riga BDFutbol Foggia 1991-92: 15 presenze, 5 partenze, 3 gol.",
  },
};

export function getPlayerProfile(player: PlayerSeason): PlayerProfile {
  return {
    ...inferProfile(player),
    ...profileOverrides[player.id],
  };
}

export function hasProfileOverride(playerId: string) {
  return playerId in profileOverrides;
}

export function getProfiledPlayerIds() {
  return Object.keys(profileOverrides);
}

export function getCardStats(player: PlayerSeason): { label: string; value: number }[] {
  const profile = getPlayerProfile(player);

  if (player.roles.includes("POR")) {
    return [
      { label: "PRE", value: profile.defending },
      { label: "RIF", value: profile.workRate },
      { label: "RIN", value: profile.passing },
      { label: "VEL", value: profile.pace },
      { label: "FIS", value: profile.physical },
      { label: "LEG", value: profile.legacy },
    ];
  }

  return [
    { label: "VEL", value: profile.pace },
    { label: "TIR", value: profile.shooting },
    { label: "PAS", value: profile.passing },
    { label: "DRI", value: profile.dribbling },
    { label: "DIF", value: profile.defending },
    { label: "FIS", value: profile.physical },
  ];
}

function inferProfile(player: PlayerSeason): PlayerProfile {
  const primaryRole = player.roles[0];
  const base = roleBase(primaryRole);
  const scoreDelta = player.score - 88;
  const goalBoost = Math.min(10, (player.goals ?? 0) / 3);
  const assistBoost = Math.min(8, (player.assists ?? 0) / 2);
  const startBoost = Math.min(5, (player.starts ?? 0) / 8);
  const appearanceBoost = Math.min(4, (player.appearances ?? 0) / 10);
  const sourceBoost = player.source ? 3 : 0;

  return {
    dataLevel: player.source ? "sourced" : "derived",
    pace: stat(base.pace + scoreDelta * 0.65 + sourceBoost),
    shooting: stat(base.shooting + scoreDelta * 0.7 + goalBoost),
    passing: stat(base.passing + scoreDelta * 0.65 + appearanceBoost + assistBoost * 0.7),
    dribbling: stat(base.dribbling + scoreDelta * 0.65),
    defending: stat(base.defending + scoreDelta * 0.75 + (primaryRole === "DIF" ? startBoost : 0)),
    physical: stat(base.physical + scoreDelta * 0.65 + startBoost),
    finishing: stat(base.finishing + scoreDelta * 0.72 + goalBoost),
    creativity: stat(base.creativity + scoreDelta * 0.65 + assistBoost + (player.roles.includes("CEN") ? 3 : 0)),
    workRate: stat(base.workRate + scoreDelta * 0.55 + startBoost + appearanceBoost),
    legacy: stat(player.score + (player.source ? 0 : 2) + (player.goals && player.goals >= 20 ? 2 : 0)),
    chemistryTags: defaultChemistryTags(player),
    sourceNote: player.source ? `Profilo derivato da riga ${player.source.name}.` : "Profilo derivato da ruolo, rating e stats disponibili.",
  };
}

function roleBase(role: Role) {
  switch (role) {
    case "POR":
      return {
        pace: 58,
        shooting: 38,
        passing: 74,
        dribbling: 56,
        defending: 88,
        physical: 82,
        finishing: 35,
        creativity: 55,
        workRate: 86,
      };
    case "DIF":
      return {
        pace: 76,
        shooting: 48,
        passing: 73,
        dribbling: 70,
        defending: 88,
        physical: 86,
        finishing: 45,
        creativity: 66,
        workRate: 84,
      };
    case "CEN":
      return {
        pace: 78,
        shooting: 76,
        passing: 86,
        dribbling: 84,
        defending: 72,
        physical: 78,
        finishing: 74,
        creativity: 88,
        workRate: 83,
      };
    case "ATT":
      return {
        pace: 86,
        shooting: 88,
        passing: 75,
        dribbling: 86,
        defending: 42,
        physical: 78,
        finishing: 90,
        creativity: 80,
        workRate: 78,
      };
  }
}

function defaultChemistryTags(player: PlayerSeason) {
  const tags = [player.club, player.country];

  if (player.goals && player.goals >= 20) {
    tags.push("Bomber");
  }

  if (player.source) {
    tags.push("Dato fonte");
  }

  if (player.roles.length > 1) {
    tags.push("Jolly");
  }

  return tags.slice(0, 3);
}

function stat(value: number) {
  return Math.max(35, Math.min(99, Math.round(value)));
}
